import React, { ChangeEvent, Dispatch, FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionCreatorWithPayload } from '@reduxjs/toolkit';
import { Content, ModalBackdrop } from 'components/ModalDialog/style';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import {
  CANCEL_ANIMATION_DELAY,
  CLOSE_AFTER_SUCCESS_SUBMIT_DELAY,
  EMPTY_STRING,
  MODAL_DIALOG_ID,
  MODAL_DIALOG_WIDTH,
  RECALCULATE_DELAY,
  ROUTE_ONE_LENDER_ID,
  STATIC_IMAGE_URLS,
  isRouteOneLender,
} from 'modules/core/constants';
import { ColumnWidth, DealSourceProvider, DealTransactionType, RequestStatus, TableName } from 'modules/core/enums';
import {
  setNetTradeIn,
  setDealSourceData as setRACDealSourceData,
  setSalesFees,
} from 'store/reducers/RACAffordabilityEngine.reducer';
import { setDealSourceData as setLenderDealSourceData } from 'store/reducers/lenders.reducer';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from 'store/store';
import { useDebounceCallback, useModalState } from 'modules/core/hooks';
import { useIsGamificationVisibleHere } from 'modules/core/hooks/useIsGamificationVisibleHere';
import { ILenderVehicle, ILTVValues, IRACVehicle } from 'modules/core/types';
import { Cell } from 'react-table';
import { IProbabilityEstimatorForm } from 'modules/ProbabilityEstimator/types';
import { produce } from 'immer';
import { costFormatter, getSubmitVehicleObjectQuery, percentFormatter, routeOneAppFormatter } from 'modules/core/utils';
import { RACAffordabilityEngineService } from 'modules/RACAffordabilityEngine/RACAffordabilityEngine.service';
import {
  LENDER_LOAN_VARIABLES_FORM_NAMES_MAPPER,
  RAC_FORM_FOR_LOAN_VARIABLES_NAMES_MAPPER,
} from 'modules/core/constants/mappers/submitVehicles.mapper';
import { getVendorName } from 'store/selectors/ui.selector';
import {
  getBalloonVehicleEnginesThunk,
  getLenderLoanTermsListThunk,
  getLenderLoanVariablesInfoThunk,
  getLenderVehicleMaxMileageThunk,
  getLoanTermsListThunk,
  getLoanVariablesInfoThunk,
  submitDealSourceDataThunk,
} from 'actions/vehicleActions';
import { logoutUserThunk } from 'actions/userActions';
import { trackActivityThunk } from 'actions/gamificationActions';
import { getAchievements, getGamificationSummary } from 'store/selectors/gamification.selector';
import { getIsGamificationEnabled } from 'store/selectors/auth.selector';
import { ProbabilityEstimatorCalculationService } from 'modules/ProbabilityEstimator/services';
import { AchievementProgressCard } from 'components/Gamification';
import { DEFAULT_MAXIMAL_PAYMENT_RANGE } from 'modules/ProbabilityEstimator/probabilityEstimator.const';
import { TableColumn } from 'modules/core/components/Table/types';
import { COLORS } from 'modules/core/theme/colors';
import { LendersMonthlyIncomeModal } from 'modules/RACAffordabilityEngine/RacMontlyIncomeModal/LendersMonthlyIncomeModal';
import { getUser } from 'store/selectors/auth.selector';
import { getCurrentLenderControls, getCurrentLenderId, getLenderState } from 'store/selectors/lenders.selector';
import { getRegionControls } from 'store/selectors/RACAffordabilityEngine.selector';
import { IDealSourceData, IDealSourceDataFlags, ISendRouteOneData } from 'modules/core/types/dealSource';
import { ILenderTierTerm } from 'modules/core/types/lender';
import { UnlockInventoryDialog } from 'components/Modals/UnlockInventoryDialog/UnlockInventoryDialog';
import { getLoanVariablesDialogState } from 'store/selectors/loanVariablesDialog.selector';
import { getControlImages } from 'store/selectors/controls.selector';
import { getVehicleState } from 'store/selectors/vehicle.selector';
import { setSelectedBalloonVehicle, setIsPendingRecalculation } from 'store/reducers/loanVariablesDialog.reducer';
import { useWindowDimensions } from 'hooks/useWindowDimensions';
import { useFormOverrideApr } from 'hooks/useFormOverrideApr';
import { useFormOverrideLtv } from 'hooks/useFormOverrideLtv';
import { ICreateUserVehicle } from 'components/Modals/AddVehicleDialog/types';
import { getRouteOneDealers } from 'store/selectors/dealer.selector';
import { ROUTE_ONE_DEALER_FIELD_NAME } from 'modules/Lender/RehashCalculator/RehashCalculator.const';
import { ADD_VEHICLE_FORM_PREFIX } from 'components/Modals/AddVehicleDialog/consts/fieldNames.const';
import { AddendumStatus } from 'modules/core/enums/addendumStatus.enum';

import {
  AdSpaceContainer,
  AdSpaceLogo,
  AdSpaceLinkOutIconWrapper,
  AdSpaceLinkOutIcon,
  MainContentWrapper,
  RightSideColumn,
} from '../styles';
import { LOAN_VARIABLES_FIELDS_NAMES, LOAN_VARIABLES_FORM_PREFIX } from '../consts/fieldNames.const';
import { SaleFeesCalculator } from '../modals/Calculators/SaleFeesCalculator/SaleFeesCalculator';
import { NetTradeInCalculator } from '../modals/Calculators/NetTradeInCalculator/NetTradeInCalculator';
import { BackAddsCalculator } from '../modals/Calculators/BackAddsCalculator/BackAddsCalculator';
import {
  AVAILABLE_DEAL_STRUCTURE_STATUSES,
  CALCULATION_FIELD_NAMES,
  DEFAULT_DAYS_TO_FIRST_PAYMENT,
  LOAN_VARIABLES_MODAL_DIALOG_ID,
  MAX_DAYS_TO_FIRST_PAYMENT,
  MIN_DAYS_TO_FIRST_PAYMENT,
} from '../consts/consts';
import { getShouldPromptResubmit } from '../utils/pricingIndicators';
import { CALCULATION_FIELDS } from '../modals/Calculators/SaleFeesCalculator/consts/fieldConfig.const';
import { ISalesFeesForm } from '../modals/Calculators/SaleFeesCalculator/types';
import { INetTradeInForm } from '../modals/Calculators/NetTradeInCalculator/types';
import { LoanVariablesFields } from './LoanVariablesFields';
import { SuggestedVehiclesBlock } from './SuggestedVehiclesBlock/SuggestedVehiclesBlock';
import { NET_TRADE_IN_CALCULATION_FIELDS } from '../modals/Calculators/NetTradeInCalculator/consts/fieldConfig.const';
// SWA-5628 Temporary hide: Lender Pricing/Market Pricing toggle from RAC page with Market Pricing state by default.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import {
  LENDER_LOAN_VARIABLES_COLUMNS,
  LENDER_LOAN_VARIABLES_CUSTOMER_RATE,
  LOAN_VARIABLES_COLUMNS_MAP,
  LOAN_VARIABLES_MODAL_FIELDS,
} from '../common.const';
import { OTHER_OPTION } from '../modals/Calculators/NetTradeInCalculator/consts/consts';
import { AddVehicleDialog } from '../../AddVehicleDialog/AddVehicleDialog';
import {
  getBalloonTerms,
  balloonToggleCallback,
  getResidualRequestParams,
  handleAddendumFlow,
  matchLoanTerms,
  getBalloonPayment,
} from '../balloonPayment/BalloonPayment.service';
import { useBalloonVehicleInfoFetchedCallback } from '../balloonPayment/hooks/useBalloonVehicleInfoFetchedCallback';
import { useRequestBalloonResidualValues } from '../balloonPayment/hooks/useRequestBalloonResidualValues';
import { useRequestBalloonFirstPaymentDays } from '../balloonPayment/hooks/useRequestBalloonFirstPaymentDays';
import { LoanVariablesModalHeader } from './LoanVariablesModalHeader/LoanVariablesModalHeader';
import { useGeneratedFields } from '../hooks/useGeneratedFields';
import { useDealStructureFieldsByRole } from '../hooks/useDealStructureFieldsByRole';
import { useHandleViewWorksheet } from '../hooks/useHandleViewWorksheet';
import {
  clearLenderList,
  getLenderListOptions,
  lenderListChangeCallback,
  validateLenderList,
} from '../lenderList/LenderList.service';
import { isDealSourceVehicleInfoMissing } from '../utils/dealSourceVehicle.utils';
import { getSubmitRouteOneLenderId } from '../utils/submitRouteOneLenderId.utils';

interface ILoanVariablesFormModal {
  modalId: string;
  isLender?: boolean;
  isRac?: boolean;
  taxExemptFields?: string[];
  currentVehicleData: IRACVehicle | ILenderVehicle | undefined;
  loanVariables: any;
  formHistory: any[];
  defaultValues: any;
  loanTermsList: number[];
  uppercaseTitle?: boolean;
  isLoanVariablesDialogActive: boolean;
  isDealSourceVehicle: boolean;
  manuallyCreated: boolean;
  handleHide: () => void;
  handleHideWithResetVehicle: () => void;
  setFormHistory: Dispatch<React.SetStateAction<any[]>>;
  setLoanVariables: ActionCreatorWithPayload<any, string>;
  setLoanTermsList: ActionCreatorWithPayload<any, string>;
  isAddedUserVehicle: boolean;
  createdUserVehicle: ICreateUserVehicle;
  dealSourceData: IDealSourceData;
  dealSourceDataFlags: IDealSourceDataFlags;
  resetDealSourceDataFlags: Function;
  modalTitle: JSX.Element;
  isSubmitR1DealBlockedByControls?: boolean;
  isBalloonPaymentAvailable: boolean;
  isEligibleForAfg: boolean;
  sortedVehiclesSelector: any;
  vehiclesFetchingFlagsSelector: any;
  isSalespersonLimitedView?: boolean;
  isMakeItWork?: boolean;
  shouldRenderRouteOneUserId?: boolean;
  getActualPaymentInfoMessage?: (loanVariables: any, availableDealStructureStatus?: any) => string;
  getLtvCategories?: (loanVariables: any, availableDealStructureStatus: any) => any[];
  isShopByCallbackMode?: boolean;
  isRouteOneFlow?: boolean;
  isRouteOneFlowForSbbOrSbc?: boolean;
  isDealerSsoSbbOrSbcFlow?: boolean;
  isDisplayPolicyStatusMessage: boolean;
  onOdometerChange?: (odometer: number | null | undefined) => void;
  isAwaitingInitialBalloonSetup?: boolean;
  onInitialBalloonSetupComplete?: () => void;
}

export const LoanVariablesFormContent: FC<ILoanVariablesFormModal> = ({
  modalId,
  isLender = false,
  isRac = false,
  taxExemptFields,
  currentVehicleData,
  loanVariables,
  defaultValues,
  formHistory,
  loanTermsList,
  uppercaseTitle = true,
  isDealSourceVehicle = false,
  manuallyCreated = false,
  handleHide,
  handleHideWithResetVehicle,
  setFormHistory,
  setLoanVariables,
  setLoanTermsList,
  isAddedUserVehicle,
  createdUserVehicle,
  dealSourceData,
  dealSourceDataFlags,
  resetDealSourceDataFlags,
  modalTitle,
  isSubmitR1DealBlockedByControls = false,
  isBalloonPaymentAvailable,
  isEligibleForAfg,
  sortedVehiclesSelector,
  vehiclesFetchingFlagsSelector,
  isSalespersonLimitedView = false,
  isMakeItWork = false,
  shouldRenderRouteOneUserId = false,
  getActualPaymentInfoMessage,
  getLtvCategories,
  isShopByCallbackMode = false,
  isRouteOneFlow = false,
  isRouteOneFlowForSbbOrSbc = false,
  isDealerSsoSbbOrSbcFlow = false,
  isDisplayPolicyStatusMessage,
  onOdometerChange,
  isAwaitingInitialBalloonSetup = false,
  onInitialBalloonSetupComplete,
}) => {
  const lenderState = useSelector(getLenderState);
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const regionControls = useSelector(getRegionControls);
  const lenderControls = useSelector(getCurrentLenderControls);
  const currentLenderId = useSelector(getCurrentLenderId);
  const vendorName = useSelector(getVendorName);
  const controlImages = useSelector(getControlImages);
  const gamificationAchievements = useSelector(getAchievements);
  const gamificationSummary = useSelector(getGamificationSummary);
  const gamificationEnabled = useSelector(getIsGamificationEnabled);
  const gamificationVisibleHere = useIsGamificationVisibleHere();
  const hasGamificationContent =
    gamificationVisibleHere && (gamificationAchievements.length > 0 || !!gamificationSummary?.closest_achievement);
  const [maxParticipation, setMaxParticipation] = useState<number | undefined>(undefined);
  const [showSuccessSubmit, setShowSuccessSubmit] = useState(false);
  const [addendumStep, setAddendumStep] = useState(0);
  const [addendumStatus, setAddendumStatus] = useState<AddendumStatus>(AddendumStatus.INACTIVE);
  const quoteIdRef = useRef<unknown>(null);
  const recalculateGenerationRef = useRef(0);
  const deferredLoanTermRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // A term picked while the deal-source vehicle was still unpriceable was nobody's choice (SWA-8055).
  const isTermProvisionalRef = useRef(false);
  const cancelDeferredLoanTerm = useCallback(() => {
    if (deferredLoanTermRef.current === null) return;

    clearTimeout(deferredLoanTermRef.current);
    deferredLoanTermRef.current = null;
  }, []);

  const [isToggled, setToggled] = useState<boolean>(false);
  const [isUndoClicked, setIsUndoClicked] = useState<boolean>(false);
  const [isAdjustDealModalAnimated, setIsAdjustDealModalAnimated] = useState(false);
  const [areInitialBalloonValuesApplied, setAreInitialBalloonValuesApplied] = useState(false);
  const isAwaitingInitialBalloonSetupRef = useRef(isAwaitingInitialBalloonSetup);
  const [showAddVehicleDialog, setShowAddVehicleDialog] = useState<boolean>(false);
  const [isShowErrorInDependentFields, setIsShowErrorInDependentFields] = useState<boolean>(false);
  const [isTaxesAffectingFields, setIsTaxesAffectingFields] = useState<boolean>(false);
  const user = useSelector(getUser);
  const isDealerTrack = user?.dealSource === DealSourceProvider.DEALER_TRACK;
  const routeOneDealers = useSelector(getRouteOneDealers);
  const { getValues: commonGetValues, setValue: commonSetValue, watch: watchFormContext } = useFormContext();
  const { tier } = commonGetValues() as any;
  const { max_allowed_pti, max_allowed_payment } = tier;
  const tierName = isLender ? tier.tier_name : tier.tier;
  const adjustDealLogo =
    (isLender && lenderState?.adjust_deal_logo) || (isRac && controlImages.racAdjustDealLogo) || null;
  const adjustDealLogoLink =
    (isLender && lenderState?.adjust_deal_logo_link) || (isRac && controlImages.racAdjustDealLogoLink) || null;
  const adjustDealLogoShouldDisplayUpgrade =
    ((isLender && lenderState?.adjust_deal_logo_display_upgrade_inventory) ||
      (isRac && controlImages.racAdjustDealLogoDisplayUpgradeInventory)) &&
    !user?.isWithInventory;
  const setDealSourceData = isLender ? setLenderDealSourceData : setRACDealSourceData;
  const [maxBackEnd, setMaxBackEnd] = useState(loanVariables.max_back_end);
  const [isTermAnimated, setIsTermAnimated] = useState<boolean>(false);
  const [isTaxesAnimated, setIsTaxesAnimated] = useState<boolean>(false);
  const [isSpinerAnimated, setIsSpinerAnimated] = useState<boolean>(false);
  const [isBalloonRowAnimated, setIsBalloonRowAnimated] = useState<boolean>(false);
  const [currentDefaultFormValues, setCurrentDefaultFormValues] = useState<any>(defaultValues);
  const [isValidLenderList, setIsValidLenderList] = useState<boolean>(true);
  const defaultParticipation = (commonGetValues().tier as any).max_participation as number;
  const {
    balloonVehicleInfoFlags,
    balloonVehicleInfo,
    balloonVehicleEngines,
    selectedBalloonVehicle,
    balloonResidualValues,
    maxMileage,
    isGeneratingWorksheet,
    isPendingRecalculation,
  } = useSelector(getLoanVariablesDialogState);
  const { balloonMaxOdometers } = useSelector(getVehicleState);
  const balloonTerms = getBalloonTerms(balloonResidualValues);
  const { isModalPageViewWidth } = useWindowDimensions();

  const formMethods = useForm({
    mode: 'onSubmit',
    shouldUnregister: false,
    defaultValues: defaultValues,
  });
  const {
    getValues,
    reset,
    setValue,
    watch,
    handleSubmit,
    trigger,
    clearErrors,
    formState: { dirtyFields, errors },
  } = formMethods;
  const {
    price,
    retailPrice,
    mileage,
    guidebookValue,
    cashDown,
    rebates,
    addOns,
    vsc,
    gap,
    taxes,
    payment,
    participation,
    loanTerm,
    loanTermsList: formLoanTermsList,
    apr,
    monthlyIncome,
    applyCpo,
    currentPayment,
    totalAmountFinanced,
    netTradeInForm,
    salesFeesForm,
    ltvCategories,
    monthlyIncomeForm,
    vehicle_id,
    routeOneUserId,
    balloonToggle,
    balloonPayment,
    afgTrim,
    engine,
    annualMileage,
    lenderList,
    createAddendum,
    afgSubmitDeal,
    contractDate,
    firstPaymentDate,
  } = watch(LOAN_VARIABLES_FORM_PREFIX) as any;
  const activeLoanTermsList = Array.isArray(formLoanTermsList) ? formLoanTermsList : loanTermsList;
  const submittedTerm = isDealSourceVehicle ? dealSourceData?.finance_details?.submitted_term : undefined;
  const selectedTerm = Number(loanTerm?.value);
  const submittedTermMismatch = useMemo(
    () => (submittedTerm && selectedTerm && selectedTerm !== submittedTerm ? submittedTerm : undefined),
    [submittedTerm, selectedTerm],
  );

  const maxPaymentValue = useCallback(
    () =>
      ProbabilityEstimatorCalculationService.getMaximalMonthlyPayment(
        max_allowed_pti,
        max_allowed_payment,
        monthlyIncome,
      ) || DEFAULT_MAXIMAL_PAYMENT_RANGE,
    [max_allowed_payment, max_allowed_pti, monthlyIncome],
  );
  const necessaryDirtyFields = Object.keys(dirtyFields?.loanVariablesForm || {}).filter((key) =>
    CALCULATION_FIELD_NAMES.includes(key),
  );

  const ltvTaxesFeesFilter = watchFormContext('preferences.ltvTaxesFeesFilter');
  const routeOneDealer = watchFormContext(ROUTE_ONE_DEALER_FIELD_NAME);

  const lenderListOptions = useMemo(() => getLenderListOptions(user?.dealer?.id, routeOneDealer, routeOneDealers), [
    user?.dealer?.id,
    routeOneDealer,
    routeOneDealers,
  ]);

  const isShowLenderList = useMemo(
    () => isLender && !!lenderState?.lender_list && !!lenderListOptions && lenderListOptions.length > 0,
    [isLender, lenderState?.lender_list, lenderListOptions],
  );

  const afgDealerId =
    (routeOneDealer?.value
      ? routeOneDealers.find((dealer) => dealer.route_one_id === routeOneDealer.value)?.afg_id
      : null) ??
    user?.afgDealerId ??
    null;

  const isRenderAddendumFields = Boolean(
    isMakeItWork && !isSubmitR1DealBlockedByControls && !isRouteOneFlowForSbbOrSbc && balloonToggle && afgDealerId,
  );

  const isDownloadAddendumStep = isRenderAddendumFields && createAddendum && !afgSubmitDeal;

  const isExtraCashAvailable = useCallback(() => {
    if (isLender) {
      return loanVariables.extra_cash_or_discount === 0;
    } else {
      return (
        ltvCategories.filter((ltvCategory: { extra_down_payment: number }) => ltvCategory.extra_down_payment === 0)
          .length === 1
      );
    }
  }, [isLender, loanVariables.extra_cash_or_discount, ltvCategories])();

  const availableDealStructureStatus = useMemo(() => {
    const isLtvDataEmpty = isLender ? !loanVariables?.ltv_ratio : loanVariables?.ltv_categories?.length === 0;
    const isCalcFieldEmpty = currentPayment === EMPTY_STRING && totalAmountFinanced === EMPTY_STRING;

    if (isLtvDataEmpty || isCalcFieldEmpty) return AVAILABLE_DEAL_STRUCTURE_STATUSES.MISSING_OR_INVALID_INFO;

    return isExtraCashAvailable
      ? AVAILABLE_DEAL_STRUCTURE_STATUSES.IN_POLICY
      : AVAILABLE_DEAL_STRUCTURE_STATUSES.OUT_OF_POLICY;
  }, [
    isLender,
    loanVariables?.ltv_ratio,
    loanVariables?.ltv_categories?.length,
    isExtraCashAvailable,
    currentPayment,
    totalAmountFinanced,
  ]);

  const isRouteOneSession = Object.keys(dealSourceData || {}).length > 0;
  const isArraFinanceRouteOneSession =
    isRouteOneSession && isRouteOneLender(lenderState?.route_one_id, ROUTE_ONE_LENDER_ID.ARRA_FINANCE);
  // RAC reads whichever lender was last visited, so the lender flag has to be part of the gate.
  const isAllyLender = isLender && isRouteOneLender(lenderState?.route_one_id, ROUTE_ONE_LENDER_ID.ALLY);
  const isAmericreditLender = isLender && isRouteOneLender(lenderState?.route_one_id, ROUTE_ONE_LENDER_ID.AMERICREDIT);
  const isMtbLender = isLender && isRouteOneLender(lenderState?.route_one_id, ROUTE_ONE_LENDER_ID.MTB);
  // SWA-8075 puts M&T on Ally's step titles and footnotes. The rate cell and hidden Est. Payment
  // already follow this same set of lenders.
  const isResubmitForPricing = isAllyLender || isAmericreditLender || isMtbLender;
  const hidesEstimatedPayment = isResubmitForPricing;
  const showsDecisionRatePrompt = isResubmitForPricing;
  // SWA-8056: M&T withholds GAP the way Ally does. VSC still submits, and Line 3 stays the RouteOne amount.
  const skipRouteOneGap = isAllyLender || isMtbLender;
  // Mirrors LoanVariablesFields: whether the modal offers Send to RouteOne rather than Print.
  const canSubmitDeal = isMakeItWork && !isSubmitR1DealBlockedByControls && !isDealerTrack;
  const canActOnAmericreditOutOfPolicy = isAmericreditLender && isMakeItWork && !isDealerTrack;
  const canSubmitDealStructure =
    availableDealStructureStatus === AVAILABLE_DEAL_STRUCTURE_STATUSES.IN_POLICY ||
    (canActOnAmericreditOutOfPolicy &&
      availableDealStructureStatus === AVAILABLE_DEAL_STRUCTURE_STATUSES.OUT_OF_POLICY);
  const isCustomerRateEnabled = isArraFinanceRouteOneSession ? isDealSourceVehicle : lenderControls.showRates;
  const shouldShowCustomerRate =
    Boolean(isCustomerRateEnabled) && (showsDecisionRatePrompt || loanVariables?.customer_rate != null);

  const shouldPromptResubmit =
    !showsDecisionRatePrompt &&
    getShouldPromptResubmit(
      isLender && isCustomerRateEnabled,
      loanVariables,
      availableDealStructureStatus,
      isRouteOneSession,
    );

  // SWA-7945: name the Max Loan threshold when Total Amount Financed is at the cap.
  // Extra cash is ceiled, so quoted TAF can sit up to $1 under Max Loan and still be a Max
  // Loan issue. Keep the generic extra-cash message only when TAF is further below (LTV, payment).
  const maxAmountFinancedWarn = useMemo(() => {
    if (!isLender || !loanVariables?.is_max_amount_financed_limited || !loanVariables?.max_amount_financed) return '';

    if (Number(totalAmountFinanced) < loanVariables.max_amount_financed - 1) return '';

    return t('components.loanVariablesModal.warningMessages.maxAmountFinancedExceeded', {
      maxAmountFinanced: costFormatter(loanVariables.max_amount_financed, false),
      extraCash: costFormatter(loanVariables.extra_cash_or_discount, false),
    });
  }, [
    isLender,
    loanVariables?.is_max_amount_financed_limited,
    loanVariables?.max_amount_financed,
    loanVariables?.extra_cash_or_discount,
    totalAmountFinanced,
    t,
  ]);

  const setHistoryCallback = useCallback(
    (overriddenValues = {}) => {
      const values = getValues();

      setFormHistory((prevHistory) =>
        produce(prevHistory, (draft) => {
          draft.push({
            loanVariablesForm: {
              ...values.loanVariablesForm,
              loanTermsList: Array.isArray(values.loanVariablesForm.loanTermsList)
                ? values.loanVariablesForm.loanTermsList
                : activeLoanTermsList,
              ...overriddenValues,
            },
          });
        }),
      );
    },
    [activeLoanTermsList, getValues, setFormHistory],
  );

  const overrideFormHistoryValues = useCallback(
    (overriddenValues) => {
      setFormHistory((prevHistory) =>
        produce(prevHistory, (draft: any) => {
          draft.forEach((item: any, index: number) => {
            draft[index] = {
              loanVariablesForm: {
                ...item.loanVariablesForm,
                ...overriddenValues,
              },
            };
          });
        }),
      );
    },
    [setFormHistory],
  );

  const {
    isFetchingBalloonResidualValues,
    residualFetchFailed,
    requestBalloonResidualValues,
  } = useRequestBalloonResidualValues(
    balloonToggle,
    modalId,
    mileage,
    engine,
    annualMileage,
    setValue,
    overrideFormHistoryValues,
    dealSourceData,
    isLender ? lenderState?.route_one_id : undefined,
  );

  // SWA-5628 Temporary hide: Lender Pricing/Market Pricing toggle from RAC page with Market Pricing state by default.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handlePriceClick = () => {
    setToggled(!isToggled);
    isToggled
      ? setValue(LOAN_VARIABLES_FIELDS_NAMES.PRICE, LOAN_VARIABLES_FIELDS_NAMES.RETAIL_PRICE)
      : setValue(LOAN_VARIABLES_FIELDS_NAMES.PRICE, LOAN_VARIABLES_FIELDS_NAMES.ADJUSTED_PRICE);
    setHistoryCallback();
  };

  const validateFormOnMount = async () => {
    const isValidForm = await trigger();

    if (!isValidForm) processInvalidForm();
  };

  useEffect(() => {
    validateFormOnMount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => cancelDeferredLoanTerm, [cancelDeferredLoanTerm]);

  useEffect(() => {
    const hasCpoEligibility = loanVariables && 'is_cpo_eligible' in loanVariables;

    if (hasCpoEligibility && !loanVariables.is_cpo_eligible && applyCpo) {
      setValue(LOAN_VARIABLES_FIELDS_NAMES.APPLY_CPO, false);
    }
  }, [loanVariables, applyCpo, setValue]);

  const shouldOpenAddVehicleDialog = useMemo(() => {
    const vin = loanVariables?.vin || currentVehicleData?.vin;

    return isDealSourceVehicleInfoMissing(isDealSourceVehicle, vin, dealSourceData);
  }, [loanVariables?.vin, currentVehicleData?.vin, dealSourceData, isDealSourceVehicle]);

  useEffect(() => {
    if (shouldOpenAddVehicleDialog) {
      isTermProvisionalRef.current = true;
      setShowAddVehicleDialog(true);
    }
  }, [shouldOpenAddVehicleDialog]);

  const addVehicleInitValues = useMemo(() => {
    const condition = loanVariables?.condition || currentVehicleData?.condition;
    const make = loanVariables?.make || currentVehicleData?.make;
    const model = loanVariables?.model || currentVehicleData?.model;

    if (shouldOpenAddVehicleDialog) {
      return {
        id: dealSourceData?.vehicle_id,
        vin: dealSourceData?.sale_vehicle?.vin,
        condition: {
          title: condition,
          value: condition,
        },
        make: {
          title: make,
          value: make,
        },
        model: {
          title: model,
          value: model,
        },
        trim: dealSourceData?.sale_vehicle?.trim,
        year: {
          title: dealSourceData?.sale_vehicle?.year,
          value: dealSourceData?.sale_vehicle?.year,
        },
        mileage: dealSourceData?.sale_vehicle?.mileage,
        retailPrice: dealSourceData?.sale_vehicle?.retail_price,
        bookValue: dealSourceData?.sale_vehicle?.book_value,
      };
    }
  }, [
    shouldOpenAddVehicleDialog,
    dealSourceData?.sale_vehicle,
    dealSourceData?.vehicle_id,
    loanVariables?.condition,
    loanVariables?.make,
    loanVariables?.model,
    currentVehicleData?.condition,
    currentVehicleData?.make,
    currentVehicleData?.model,
  ]);

  const {
    isModalDialogActive: isModalSalesFeesDialogActive,
    handleOpenDialog: handleSalesFeesDialogOpen,
  } = useModalState([MODAL_DIALOG_ID.LOAN_VARIABLES, LOAN_VARIABLES_MODAL_DIALOG_ID.SALES_FEES_CALCULATOR]);

  const {
    isModalDialogActive: isIncomeDialogActive,
    handleOpenDialog: handleIncomeDialogOpen,
  } = useModalState<IProbabilityEstimatorForm>([
    MODAL_DIALOG_ID.LOAN_VARIABLES,
    LOAN_VARIABLES_MODAL_DIALOG_ID.MOUNTLY_INCOME_CALCULATOR,
  ]);

  const { handleCloseDialog: handleIncomeDialogClose } = useModalState<IProbabilityEstimatorForm>([
    MODAL_DIALOG_ID.LOAN_VARIABLES,
    LOAN_VARIABLES_MODAL_DIALOG_ID.MOUNTLY_INCOME_CALCULATOR,
  ]);

  const { handleCloseDialog: handleSalesFeesDialogClose } = useModalState([
    MODAL_DIALOG_ID.LOAN_VARIABLES,
    LOAN_VARIABLES_MODAL_DIALOG_ID.SALES_FEES_CALCULATOR,
  ]);

  const {
    isModalDialogActive: isNetTradeInDialogOpen,
    handleOpenDialog: handleNetTradeInDialogOpen,
    handleCloseDialog: handleCloseNetTradeInDialog,
  } = useModalState<IProbabilityEstimatorForm>([
    MODAL_DIALOG_ID.LOAN_VARIABLES,
    LOAN_VARIABLES_MODAL_DIALOG_ID.NET_TRADE_IN_CALCULATOR,
  ]);

  const {
    isModalDialogActive: isBackAddsDialogActive,
    handleOpenDialog: handleBackAddsDialogOpen,
    handleCloseDialog: handleBackAddsDialogClose,
  } = useModalState([MODAL_DIALOG_ID.LOAN_VARIABLES, LOAN_VARIABLES_MODAL_DIALOG_ID.BACK_ADDS_CALCULATOR]);

  const {
    isModalDialogActive: isUnlockInventoryDialogActive,
    handleOpenDialog: handleUnlockInventoryDialogOpen,
    handleCloseDialog: handleUnlockInventoryDialogClose,
  } = useModalState([MODAL_DIALOG_ID.LOAN_VARIABLES, LOAN_VARIABLES_MODAL_DIALOG_ID.UNLOCK_INVENTORY]);

  const termsMatchedWithBalloon = useMemo(() => matchLoanTerms(activeLoanTermsList, balloonTerms), [
    activeLoanTermsList,
    balloonTerms,
  ]);
  const defaultLoanTermForBalloon =
    termsMatchedWithBalloon.length > 0
      ? { title: termsMatchedWithBalloon[0], value: termsMatchedWithBalloon[0] }
      : { title: undefined, value: undefined };
  const [loanTermPerTab, setLoanTermPerTab] = useState<any>({
    retail: defaultValues.loanVariablesForm.loanTerm,
    balloon: termsMatchedWithBalloon.includes(defaultValues.loanVariablesForm.loanTerm.value)
      ? defaultValues.loanVariablesForm.loanTerm
      : defaultLoanTermForBalloon,
  });
  const [firstPaymentDaysRange, setFirstPaymentDaysRange] = useState<{ default: number; min: number; max: number }>({
    default: DEFAULT_DAYS_TO_FIRST_PAYMENT,
    min: MIN_DAYS_TO_FIRST_PAYMENT,
    max: MAX_DAYS_TO_FIRST_PAYMENT,
  });

  const getRequestParams = useCallback(() => {
    const {
      addOns,
      retailPrice,
      vsc,
      gap,
      loanTerm,
      guidebookValue,
      netTradeInForm,
      totalSalesFees,
      payment,
      mileage,
      cashDown,
      rebates,
      vehicle_id,
      participation,
      applyCpo,
    } = getValues().loanVariablesForm;

    return {
      vehicle_id: vehicle_id,
      add_ons: addOns || 0,
      current_price: retailPrice || 0,
      vsc: vsc || 0,
      gap: skipRouteOneGap ? 0 : gap || 0,
      loan_term: loanTerm.value || 0,
      guidebook_value: guidebookValue || 0,
      apply_cpo: isLender ? Boolean(applyCpo) : undefined,
      trade_value: netTradeInForm.tradeInValue || 0,
      sales_fees: isLender ? totalSalesFees || 0 : undefined,
      estimated_fees: !isLender ? totalSalesFees || 0 : undefined,
      approved_monthly_payment: payment || 0,
      odometer: mileage || 0,
      cash_down_payment: (cashDown || 0) + (rebates || 0),
      vsc_and_gap_insurance: (vsc || 0) + (skipRouteOneGap ? 0 : gap || 0),
      amount_owed: netTradeInForm.amountOwnedOnTrade || 0,
      participation: participation || 0,
    };
  }, [getValues, isLender, skipRouteOneGap]);

  const handleAdjustDealLogoClick = useCallback(() => {
    if (adjustDealLogoShouldDisplayUpgrade) {
      const unlockUrl = controlImages.unlockInventoryUrl;

      if (unlockUrl) {
        window.open(unlockUrl, '_blank');
      } else {
        handleUnlockInventoryDialogOpen();
      }
    } else if (adjustDealLogoLink) {
      window.open(adjustDealLogoLink, '_blank');
    }
  }, [
    adjustDealLogoShouldDisplayUpgrade,
    adjustDealLogoLink,
    controlImages.unlockInventoryUrl,
    handleUnlockInventoryDialogOpen,
  ]);

  const { hasFormOverrideApr, getFormOverrideApr, getOverrideApr } = useFormOverrideApr(commonGetValues);
  const { getLtvHighLimit } = useFormOverrideLtv(commonGetValues);

  const getOverrideAprForLoanVariablesRequest = useCallback(() => {
    if (!isLender) return undefined;

    const { balloonToggle } = getValues().loanVariablesForm;

    return getOverrideApr({
      balloonToggle,
      isShopByCallbackMode,
      dealSourceOverrideApr: dealSourceData?.finance_details?.override_apr,
      dealSourceTransactionType: dealSourceData?.transaction_type,
    });
  }, [
    isLender,
    isShopByCallbackMode,
    getValues,
    dealSourceData?.finance_details?.override_apr,
    dealSourceData?.transaction_type,
    getOverrideApr,
  ]);

  const getAprForLoanVariablesRequest = useCallback(() => {
    if (isLender && isShopByCallbackMode) {
      return commonGetValues().shopByCallback?.sbcApr;
    }

    const { balloonToggle } = getValues().loanVariablesForm;

    if (isLender) {
      if (
        hasFormOverrideApr() &&
        !balloonToggle &&
        dealSourceData?.transaction_type !== DealTransactionType.BALLOON_PAYMENT
      ) {
        return getFormOverrideApr();
      }
    }

    const isDealSourceAprApplicable =
      (dealSourceData?.finance_details?.apr || !!dealSourceData?.finance_details?.override_apr) &&
      !balloonToggle &&
      dealSourceData?.transaction_type !== DealTransactionType.BALLOON_PAYMENT;

    return isDealSourceAprApplicable ? dealSourceData?.finance_details?.apr : undefined;
  }, [
    isLender,
    isShopByCallbackMode,
    getValues,
    dealSourceData?.finance_details?.override_apr,
    dealSourceData?.finance_details?.apr,
    dealSourceData?.transaction_type,
    hasFormOverrideApr,
    getFormOverrideApr,
    commonGetValues,
  ]);

  const handleViewWorksheet = useHandleViewWorksheet({
    modalId,
    currentVehicleData,
    loanTermsList: activeLoanTermsList,
    isDealSourceVehicle,
    isAddedUserVehicle,
    createdUserVehicle,
    dealSourceData,
    ltvTaxesFeesFilter,
    balloonResidualValues,
    balloonTerms: balloonTerms || [],
    isShopByCallbackMode,
    getRequestParams,
    getValues,
    getAprForLoanVariablesRequest,
    getOverrideAprForLoanVariablesRequest,
  });

  const getLoanVariablesInfo = useCallback(
    async (params?: any, dealSourceData?: any) => {
      const requestParams = getRequestParams();
      const vehicleParameters = getSubmitVehicleObjectQuery(
        { ...currentVehicleData, ...commonGetValues(), vendorName: vendorName, isLender },
        RACAffordabilityEngineService.getSubmitQueryForLoanVariables,
        isLender ? LENDER_LOAN_VARIABLES_FORM_NAMES_MAPPER : RAC_FORM_FOR_LOAN_VARIABLES_NAMES_MAPPER,
      );

      let loanVariablesInfoResponse: { payload: any };

      if (isLender) {
        const vehicleYear = isAddedUserVehicle ? createdUserVehicle?.year : currentVehicleData?.year;
        const r1DealerId =
          (user?.isWithInventory
            ? (commonGetValues() as any).routeOneDealer?.value
            : (commonGetValues() as any).routeOneDealerId) ||
          user?.originalExternalDealerId ||
          undefined;

        loanVariablesInfoResponse = await dispatch(
          getLenderLoanVariablesInfoThunk({
            ...vehicleParameters,
            ...requestParams,
            ...params,
            bank_discount: commonGetValues().feesAndTaxes.bankDiscount,
            bank_fees: commonGetValues().feesAndTaxes.bankFees,
            lender_name: commonGetValues().lenderName,
            is_ltv: ltvTaxesFeesFilter,
            deal_source_ltv_high_limit: dealSourceData?.finance_details?.high_ltv_limit,
            ltv_high_limit: getLtvHighLimit({
              isShopByCallbackMode,
              sbcLtv: (commonGetValues() as any).shopByCallback?.sbcLtv,
              tierLtvLimit: (commonGetValues().tier as any)?.ltv_limit,
              dealSourceLtv: dealSourceData?.finance_details?.high_ltv_limit,
            }),
            monthly_income: commonGetValues().grossMonthlyIncome,
            targeted_payment: requestParams.approved_monthly_payment,
            year: isDealSourceVehicle ? parseInt(dealSourceData?.sale_vehicle?.year) : vehicleYear,
            apr: getAprForLoanVariablesRequest(),
            override_apr: getOverrideAprForLoanVariablesRequest(),
            override_vsc: dealSourceData?.finance_details?.override_vsc,
            total_ltv_limit: dealSourceData?.finance_details?.total_ltv_limit,
            include_calc_monthly_payment: isShopByCallbackMode,
            is_deal_structure_modified: params?.is_deal_structure_modified ?? true,
            is_override_apr_entered: hasFormOverrideApr(),
            ...(r1DealerId && { r1_dealer_id: r1DealerId }),
          }),
        );
      } else {
        loanVariablesInfoResponse = await dispatch(
          getLoanVariablesInfoThunk({
            ...vehicleParameters,
            ...requestParams,
            ...params,
            ...(isMakeItWork && { deal_date: dealSourceData?.submitted_date_time }),
          }),
        );
      }

      return loanVariablesInfoResponse.payload;
    },
    [
      commonGetValues,
      createdUserVehicle?.year,
      currentVehicleData,
      dispatch,
      isAddedUserVehicle,
      isLender,
      isDealSourceVehicle,
      ltvTaxesFeesFilter,
      getRequestParams,
      vendorName,
      getAprForLoanVariablesRequest,
      getOverrideAprForLoanVariablesRequest,
      hasFormOverrideApr,
      getLtvHighLimit,
      isShopByCallbackMode,
      isMakeItWork,
      user?.isWithInventory,
      user?.originalExternalDealerId,
    ],
  );

  const getApr = (isLender: boolean, isLtvCategoriesExists: boolean, loanVariables: any, balloonToggle: boolean) => {
    let apr = NaN;

    if (
      isLender &&
      hasFormOverrideApr() &&
      !balloonToggle &&
      dealSourceData?.transaction_type !== DealTransactionType.BALLOON_PAYMENT
    ) {
      apr = getFormOverrideApr();
    } else if (
      isLender &&
      !!dealSourceData?.finance_details?.override_apr &&
      dealSourceData?.finance_details?.apr &&
      !balloonToggle &&
      dealSourceData?.transaction_type !== DealTransactionType.BALLOON_PAYMENT
    ) {
      apr = dealSourceData?.finance_details?.apr;
    } else if (isLender) {
      apr = loanVariables?.customer_rate - participation;
    } else if (isLtvCategoriesExists) {
      apr = loanVariables.ltv_categories[0].rate;
    }

    return apr;
  };

  const resetLoanVariablesFormValues = useCallback(
    (overriddenValues = {}) => {
      const loanVariablesFormValues = getValues().loanVariablesForm;

      reset({
        loanVariablesForm: {
          ...loanVariablesFormValues,
          ...overriddenValues,
        },
      });
    },
    [getValues, reset],
  );

  const startRecalculateAnimations = useCallback((isTaxesRecalculateRequired, isBalloonRowAnimated) => {
    setIsAdjustDealModalAnimated(true);
    setIsTaxesAnimated(isTaxesRecalculateRequired);
    setIsBalloonRowAnimated(isBalloonRowAnimated);
    setTimeout(() => {
      setIsSpinerAnimated(true);
    }, CANCEL_ANIMATION_DELAY / 4);
  }, []);

  const stopRecalculateAnimations = useCallback(() => {
    setIsAdjustDealModalAnimated(false);
    setIsSpinerAnimated(false);
    setIsTermAnimated(false);
    setIsTaxesAnimated(false);
    setIsBalloonRowAnimated(false);
  }, []);

  const recalculateLoanVariables = useCallback(
    async ({
      isTaxesRecalculateRequired = false,
      params = {},
      cursorData = {},
      activeElement = null,
      dealSourceData = {},
      balloonResidualValues,
      refreshLoanTermsList = false,
    }) => {
      const { field, cursorPosition } = cursorData;

      clearErrors();
      dispatch(resetDealSourceDataFlags());
      setIsShowErrorInDependentFields(false);

      const minLoaderDuration = new Promise<void>((resolve) => {
        // The recalculate animation is not on screen yet while the modal is still behind its own
        // loader, so keeping it for a minimum duration would only postpone opening the modal.
        if (isAwaitingInitialBalloonSetupRef.current) {
          resolve();

          return;
        }

        setTimeout(() => {
          resolve();
        }, CANCEL_ANIMATION_DELAY);
      });

      const loanVariablesFormValues = getValues().loanVariablesForm;
      const { guidebookValue, taxes, balloonToggle, annualMileage } = loanVariablesFormValues;
      const balloonTerms = getBalloonTerms(balloonResidualValues);
      const activeBalloonResidualValues = balloonToggle
        ? balloonResidualValues?.perAnnualMileage?.[annualMileage?.value]?.perTerm
        : undefined;

      if (isLender && balloonToggle && Object.keys(activeBalloonResidualValues ?? {}).length === 0) {
        stopRecalculateAnimations();

        return;
      }

      const loanVariablesInfoResponse: any = await getLoanVariablesInfo(
        {
          ...params,
          unit_cost: guidebookValue,
          ...(!isTaxesRecalculateRequired && { taxes: taxes }),
          ...(isLender && { include_eligible_terms: true }),
          ...(balloonToggle && {
            balloon_terms: balloonTerms,
            balloon_residual_values: activeBalloonResidualValues,
          }),
        },
        dealSourceData,
      );
      const isLtvCategoriesExists =
        (isLender && loanVariablesInfoResponse?.ltv_ratio) || loanVariablesInfoResponse?.ltv_categories?.length > 0;
      const nextTotalAmountFinanced = isLtvCategoriesExists
        ? isLender
          ? loanVariablesInfoResponse?.total_financed
          : loanVariablesInfoResponse.ltv_categories[0].loan_amount
        : 0;
      const resetData = {
        loanVariablesForm: {
          ...loanVariablesFormValues,
          ltvCategories: isLtvCategoriesExists
            ? isLender
              ? loanVariablesInfoResponse
              : loanVariablesInfoResponse.ltv_categories
            : [],
          currentPayment: isLtvCategoriesExists
            ? isLender
              ? loanVariablesInfoResponse?.calc_monthly_payment ?? loanVariablesInfoResponse?.monthly_payment
              : loanVariablesInfoResponse.ltv_categories[0].payments
            : 0,
          totalAmountFinanced: nextTotalAmountFinanced,
          lineThree: isLtvCategoriesExists
            ? loanVariablesInfoResponse?.total_financed_exc_back_end ?? nextTotalAmountFinanced
            : nextTotalAmountFinanced,
          apr: getApr(isLender, isLtvCategoriesExists, loanVariablesInfoResponse, balloonToggle),
          taxes: loanVariablesInfoResponse?.taxes || 0,
          ...(params?.loan_term !== undefined && {
            loanTerm: { title: params.loan_term, value: params.loan_term },
          }),
          ...(balloonToggle && { balloonPayment: params?.balloon_payment }),
        },
      };

      const skipLoanTermsList = params?.loan_term !== undefined && !refreshLoanTermsList;
      const resetDataWithValidatedTerms = await validateTerms(
        loanVariablesInfoResponse,
        resetData,
        skipLoanTermsList,
        dealSourceData,
        balloonTerms,
        refreshLoanTermsList,
      );

      if (balloonToggle) {
        const validatedLoanTerm = resetDataWithValidatedTerms.loanVariablesForm.loanTerm?.value;
        const validatedBalloonPayment = activeBalloonResidualValues?.[validatedLoanTerm];

        if (validatedBalloonPayment !== undefined) {
          resetDataWithValidatedTerms.loanVariablesForm.balloonPayment = validatedBalloonPayment;
        }
      }

      cancelDeferredLoanTerm();
      reset(resetDataWithValidatedTerms);
      setCurrentDefaultFormValues(resetDataWithValidatedTerms);

      await Promise.all([minLoaderDuration]);

      stopRecalculateAnimations();
      setMaxBackEnd(loanVariablesInfoResponse.max_back_end);
      dispatch(setLoanVariables({ modalId: modalId, loanVariables: loanVariablesInfoResponse }));
      setIsUndoClicked(false);
      setIsTaxesAffectingFields(false);

      if (activeElement && field && activeElement === field && Number.isInteger(cursorPosition)) {
        field.focus();
        field.setSelectionRange(cursorPosition, cursorPosition);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getValues, isLender, getLoanVariablesInfo, setLoanVariables, reset, clearErrors, balloonTerms],
  );

  const isFieldChanged = useCallback(
    (name: any) => {
      const loanVariablesForm = getValues().loanVariablesForm as any;

      const prevValues =
        formHistory.length <= 1
          ? defaultValues.loanVariablesForm
          : formHistory[formHistory.length - 1].loanVariablesForm;

      if (
        loanVariablesForm[name] &&
        typeof loanVariablesForm[name] === 'object' &&
        'value' in loanVariablesForm[name]
      ) {
        return loanVariablesForm[name].value !== prevValues[name].value;
      }

      if (Number.isNaN(prevValues[name]) && Number.isNaN(loanVariablesForm[name])) {
        return false;
      }

      return prevValues[name] !== loanVariablesForm[name];
    },
    [defaultValues.loanVariablesForm, formHistory, getValues],
  );

  const processInvalidForm = useCallback(
    (overriddenValues = {}) => {
      setValue(LOAN_VARIABLES_FIELDS_NAMES.CURRENT_PAYMENT, '');
      setValue(LOAN_VARIABLES_FIELDS_NAMES.TOTAL_AMOUNT_FINANCED, '');
      setValue(LOAN_VARIABLES_FIELDS_NAMES.LINE_THREE, '');
      setIsShowErrorInDependentFields(true);
      resetLoanVariablesFormValues(overriddenValues);
    },
    [setValue, resetLoanVariablesFormValues],
  );

  const showErrorInDependentFields = useCallback(
    async (errorFields, cursorData, dirtyFields = [], overriddenValues = {}) => {
      const activeElement = document.activeElement;
      const isMileageError = errorFields.includes(LOAN_VARIABLES_FIELDS_NAMES.MILEAGE);
      const isAfgTrimChanged = Object.keys(dirtyFields).includes('afgTrim');

      isMileageError && setIsTermAnimated(true);
      startRecalculateAnimations(errorFields.includes(LOAN_VARIABLES_FIELDS_NAMES.RETAIL_PRICE), isAfgTrimChanged);

      const animationsPromise = new Promise<void>((resolve) => {
        setTimeout(() => {
          isMileageError && setIsTermAnimated(false);
          stopRecalculateAnimations();

          const { field, cursorPosition } = cursorData;

          if (activeElement && field && activeElement === field && Number.isInteger(cursorPosition)) {
            field.focus();
            field.setSelectionRange(cursorPosition, cursorPosition);
          }

          resolve();
        }, CANCEL_ANIMATION_DELAY);
      });

      const invalidFormPromise = new Promise<void>((resolve) => {
        setTimeout(() => {
          processInvalidForm(overriddenValues);

          if (errorFields.includes(LOAN_VARIABLES_FIELDS_NAMES.RETAIL_PRICE)) {
            setValue(LOAN_VARIABLES_FIELDS_NAMES.TAXES, '');
          }

          if (isMileageError) {
            setValue(LOAN_VARIABLES_FIELDS_NAMES.LOAN_TERM, {
              title: undefined,
              value: undefined,
            });
            setValue(LOAN_VARIABLES_FIELDS_NAMES.LOAN_TERMS_LIST, []);
            dispatch(setLoanTermsList({ modalId: modalId, loanTermsList: [] }));
          }

          resolve();
        }, CANCEL_ANIMATION_DELAY / 2);
      });

      await Promise.all([animationsPromise, invalidFormPromise]);
    },

    [
      setValue,
      processInvalidForm,
      startRecalculateAnimations,
      stopRecalculateAnimations,
      dispatch,
      setLoanTermsList,
      modalId,
    ],
  );

  const getLoanTermsList = useCallback(
    async (manageAnimation = true, loanTermParam = null, dealSourceData?: any, balloonTerms?: number[]) => {
      cancelDeferredLoanTerm();

      const { mileage, loanTerm, balloonToggle } = getValues().loanVariablesForm;
      let loanTermsListResponse: any;

      if (!Number.isNaN(mileage)) {
        if (isLender) {
          const vehicleYear = isAddedUserVehicle ? createdUserVehicle?.year : currentVehicleData?.year;
          const r1DealerId =
            (user?.isWithInventory
              ? (commonGetValues() as any).routeOneDealer?.value
              : (commonGetValues() as any).routeOneDealerId) ||
            user?.originalExternalDealerId ||
            undefined;
          const conversationId = dealSourceData?.conversation_id;
          const termParams = {
            mileage: mileage,
            tier: (commonGetValues().tier as any).tier_name,
            year: isDealSourceVehicle ? parseInt(dealSourceData?.sale_vehicle?.year) : vehicleYear,
            lender_name: commonGetValues().lenderName,
            balloon_payment: balloonToggle,
            ...(r1DealerId && { r1_dealer_id: r1DealerId }),
            ...(conversationId && { conversation_id: conversationId }),
          };

          loanTermsListResponse = await dispatch(getLenderLoanTermsListThunk(termParams));

          if (loanTermsListResponse.payload.length === 0) {
            await dispatch(
              getLenderVehicleMaxMileageThunk({
                modalId: modalId,
                params: {
                  lender_name: termParams.lender_name,
                  tier: termParams.tier,
                  year: termParams.year,
                },
              }),
            );
          }
        } else {
          loanTermsListResponse = await dispatch(
            getLoanTermsListThunk({
              mileage: mileage,
              tier: tierName,
              ...(isMakeItWork && { deal_date: dealSourceData?.submitted_date_time }),
            }),
          );
        }

        dispatch(setLoanTermsList({ modalId: modalId, loanTermsList: loanTermsListResponse.payload }));

        if (manageAnimation) {
          setIsTermAnimated(true);
        }

        const loanTermValue = loanTermParam || loanTerm.value;
        const actualLoanTerms = balloonToggle
          ? matchLoanTerms(loanTermsListResponse.payload, balloonTerms)
          : loanTermsListResponse.payload;
        const shouldChange = !actualLoanTerms.includes(loanTermValue);
        const actualLoanTerm = shouldChange ? actualLoanTerms[0] : loanTermValue;

        if (shouldChange) {
          setValue(LOAN_VARIABLES_FIELDS_NAMES.LOAN_TERMS_LIST, loanTermsListResponse.payload);

          deferredLoanTermRef.current = setTimeout(() => {
            deferredLoanTermRef.current = null;

            setValue(LOAN_VARIABLES_FIELDS_NAMES.LOAN_TERM, {
              title: actualLoanTerm,
              value: actualLoanTerm,
            });

            setValue(LOAN_VARIABLES_FIELDS_NAMES.LOAN_TERMS_LIST, loanTermsListResponse.payload);
          }, CANCEL_ANIMATION_DELAY / 2);
        } else {
          setValue(LOAN_VARIABLES_FIELDS_NAMES.LOAN_TERMS_LIST, loanTermsListResponse.payload);
        }

        return {
          loanTerm: actualLoanTerm,
          loanTermsList: loanTermsListResponse.payload,
        };
      }
    },
    [
      cancelDeferredLoanTerm,
      setValue,
      getValues,
      isLender,
      setLoanTermsList,
      isAddedUserVehicle,
      createdUserVehicle?.year,
      currentVehicleData?.year,
      dispatch,
      commonGetValues,
      isDealSourceVehicle,
      tierName,
      modalId,
      isMakeItWork,
      user?.isWithInventory,
      user?.originalExternalDealerId,
    ],
  );

  const validateTerms = useCallback(
    async (
      loanVariablesInfoResponse: any,
      resetData: any,
      skipLoanTermsList = false,
      dealSourceData?: any,
      balloonTerms?: number[],
      refreshLoanTermsList = false,
    ) => {
      const { loanTerm } = resetData.loanVariablesForm;
      const loanTermParam = loanTerm?.value;
      const loanTermResponse = loanVariablesInfoResponse.term;
      const eligibleLoanTerms = loanVariablesInfoResponse?.eligible_loan_terms;

      if (isLender && Array.isArray(eligibleLoanTerms)) {
        const validatedLoanTerms = eligibleLoanTerms as number[];
        const responseTerm = validatedLoanTerms.includes(loanTermResponse) ? loanTermResponse : undefined;
        const retainedTerm = validatedLoanTerms.includes(loanTermParam) ? loanTermParam : undefined;
        const selectedTerm = responseTerm ?? retainedTerm ?? validatedLoanTerms[0];
        const selectedTermOption = {
          title: selectedTerm ?? EMPTY_STRING,
          value: selectedTerm ?? EMPTY_STRING,
        };
        const currentLoanTerms = resetData.loanVariablesForm.loanTermsList ?? activeLoanTermsList;
        const didTermsChange = JSON.stringify(currentLoanTerms) !== JSON.stringify(validatedLoanTerms);

        resetData.loanVariablesForm.loanTermsList = validatedLoanTerms;
        resetData.loanVariablesForm.loanTerm = selectedTermOption;
        setValue(LOAN_VARIABLES_FIELDS_NAMES.LOAN_TERMS_LIST, validatedLoanTerms);
        setValue(LOAN_VARIABLES_FIELDS_NAMES.LOAN_TERM, selectedTermOption);
        dispatch(setLoanTermsList({ modalId, loanTermsList: validatedLoanTerms }));
        setLoanTermPerTab((previousTerms: any) => ({
          ...previousTerms,
          [resetData.loanVariablesForm.balloonToggle ? 'balloon' : 'retail']: selectedTermOption,
        }));

        if (didTermsChange || loanTermParam !== selectedTerm) {
          setIsTermAnimated(true);
        }

        if (validatedLoanTerms.length === 0) {
          resetData.loanVariablesForm.currentPayment = EMPTY_STRING;
          resetData.loanVariablesForm.totalAmountFinanced = EMPTY_STRING;
          resetData.loanVariablesForm.lineThree = EMPTY_STRING;
          resetData.loanVariablesForm.apr = NaN;
          resetData.loanVariablesForm.ltvCategories = [];
        }

        return resetData;
      }

      if (loanTermParam && loanTermResponse && loanTermParam !== loanTermResponse) {
        resetData.loanVariablesForm.loanTerm = {
          title: loanTermResponse,
          value: loanTermResponse,
        };
        setIsTermAnimated(true);

        if (!skipLoanTermsList) {
          const refreshedTerms = await getLoanTermsList(false, null, dealSourceData, balloonTerms);

          if (refreshedTerms) {
            resetData.loanVariablesForm.loanTermsList = refreshedTerms.loanTermsList;
          }

          await new Promise((resolve) => setTimeout(resolve, CANCEL_ANIMATION_DELAY / 2));
        }
      } else if (!skipLoanTermsList && refreshLoanTermsList) {
        const refreshedTerms = await getLoanTermsList(false, null, dealSourceData, balloonTerms);

        if (refreshedTerms) {
          resetData.loanVariablesForm.loanTerm = {
            title: refreshedTerms.loanTerm,
            value: refreshedTerms.loanTerm,
          };
          resetData.loanVariablesForm.loanTermsList = refreshedTerms.loanTermsList;
        }
      }

      return resetData;
    },
    [activeLoanTermsList, dispatch, getLoanTermsList, isLender, modalId, setLoanTermsList, setValue],
  );

  const recalculateLoanData = useCallback(
    async (errorFields, dirtyFields, recalculateData, skipLoanVariablesRecalc = false) => {
      const { balloonToggle, annualMileage, loanTerm } = getValues().loanVariablesForm;
      const isMileageChanged = Object.keys(dirtyFields).includes('mileage');
      const isBalloonToggleChanged = Object.keys(dirtyFields).includes('balloonToggle');
      const isLoanTermErrorAfterToggle =
        isBalloonToggleChanged && errorFields.includes(LOAN_VARIABLES_FIELDS_NAMES.LOAN_TERM);
      const {
        isTaxesRecalculateRequired = false,
        isResidualsRequestRequired,
        balloonResidualValues,
        residualRequestParams,
        isBalloonRelatedChange = false,
      } = recalculateData;
      let actualAnnualMileage = annualMileage;

      recalculateData.activeElement = document.activeElement;
      startRecalculateAnimations(isTaxesRecalculateRequired, isBalloonRelatedChange);
      recalculateData.params = recalculateData.params || {};

      if (
        balloonToggle &&
        residualRequestParams &&
        (isResidualsRequestRequired || !balloonResidualValues || !actualAnnualMileage?.value)
      ) {
        const { residualValues, annualMileage: resolvedAnnualMileage } = await requestBalloonResidualValues(
          residualRequestParams,
        );

        recalculateData.balloonResidualValues = residualValues;
        // The form is the source of truth across the await; the value read before it is stale.
        actualAnnualMileage = resolvedAnnualMileage ?? getValues().loanVariablesForm.annualMileage;
      }

      const balloonTerms = getBalloonTerms(recalculateData.balloonResidualValues);

      const shouldRefreshLoanTerms = isMileageChanged || isLoanTermErrorAfterToggle || isBalloonToggleChanged;
      const shouldFetchLoanTermsBeforeCalculation = shouldRefreshLoanTerms && (!isLender || balloonToggle);

      if (shouldFetchLoanTermsBeforeCalculation) {
        const loanTermResult = await getLoanTermsList(
          true,
          recalculateData.params.loan_term,
          recalculateData.dealSourceData,
          balloonTerms,
        );

        if (!loanTermResult?.loanTerm) {
          showErrorInDependentFields([], recalculateData.cursorData);
          setIsTaxesAffectingFields(recalculateData.isTaxesRecalculateRequired);

          return;
        }

        recalculateData.params.loan_term = loanTermResult.loanTerm;
      }

      recalculateData.refreshLoanTermsList = shouldRefreshLoanTerms && !shouldFetchLoanTermsBeforeCalculation;

      if (balloonToggle) {
        let actualLoanTerm = recalculateData.params.loan_term || loanTerm?.value;
        const { loanTermsList: formLoanTermsList } = getValues().loanVariablesForm;
        const actualLoanTermsList = formLoanTermsList.length > 0 ? formLoanTermsList : activeLoanTermsList;
        const termsMatchedWithBalloon = matchLoanTerms(actualLoanTermsList, balloonTerms);

        if (!termsMatchedWithBalloon.includes(actualLoanTerm)) {
          setIsTermAnimated(true);
          actualLoanTerm = termsMatchedWithBalloon[0];

          if (!actualLoanTerm) {
            setValue(LOAN_VARIABLES_FIELDS_NAMES.LOAN_TERM, {
              title: undefined,
              value: undefined,
            });
            showErrorInDependentFields([], recalculateData.cursorData);
            setIsTaxesAffectingFields(recalculateData.isTaxesRecalculateRequired);

            return;
          }
        }

        if (!actualAnnualMileage) {
          setValue(LOAN_VARIABLES_FIELDS_NAMES.LOAN_TERM, {
            title: actualLoanTerm,
            value: actualLoanTerm,
          });
          showErrorInDependentFields([], recalculateData.cursorData);
          setIsTaxesAffectingFields(recalculateData.isTaxesRecalculateRequired);

          return;
        }

        const balloonPayment = getBalloonPayment(recalculateData, actualAnnualMileage.value, actualLoanTerm);

        recalculateData.params.loan_term = actualLoanTerm;
        recalculateData.params.balloon_payment = balloonPayment;
      }

      if (skipLoanVariablesRecalc) {
        if (recalculateData.params.loan_term !== undefined) {
          setValue(LOAN_VARIABLES_FIELDS_NAMES.LOAN_TERM, {
            title: recalculateData.params.loan_term,
            value: recalculateData.params.loan_term,
          });
        }

        await new Promise((resolve) => setTimeout(resolve, CANCEL_ANIMATION_DELAY));

        stopRecalculateAnimations();

        return;
      }

      await recalculateLoanVariables(recalculateData);
    },
    [
      getValues,
      recalculateLoanVariables,
      getLoanTermsList,
      showErrorInDependentFields,
      requestBalloonResidualValues,
      startRecalculateAnimations,
      stopRecalculateAnimations,
      setValue,
      activeLoanTermsList,
      isLender,
    ],
  );

  const recalculateLoanVariablesCallback = async (debouncedData: any) => {
    const { dirtyFields = {}, errorFields = [], isRecalculateRequired = false, data = {} } = debouncedData;
    const { currentPayment, totalAmountFinanced, taxes, loanTerm, mileage } = getValues().loanVariablesForm;
    const isSetErrorInCalcFields = errorFields.length !== 0 && (!!currentPayment || !!totalAmountFinanced);
    const isSetErrorInTaxes = errorFields.includes(LOAN_VARIABLES_FIELDS_NAMES.RETAIL_PRICE) && !!taxes;
    const isSetErrorInMileage = errorFields.includes(LOAN_VARIABLES_FIELDS_NAMES.MILEAGE) && !!loanTerm.value;
    const isAfgTrimChanged = Object.keys(dirtyFields).includes('afgTrim');
    const isBalloonToggleChanged = Object.keys(dirtyFields).includes('balloonToggle');
    let overriddenValues;

    // Only a reprice that really runs settles the term. Scheduling one does not: the debounce
    // keeps just the last payload, so an earlier keystroke's call is replaced, never sent, and
    // spending the latch on it would leave the request that does run without the deal source's
    // term (PR #1488 review).
    if (isRecalculateRequired && data.isTermProvisional) {
      isTermProvisionalRef.current = false;
    }

    if (isAfgTrimChanged) {
      const engineRequestParams = {
        model_year: data.actualBalloonVehicle.modelYear,
        make: data.actualBalloonVehicle.make,
        model: data.actualBalloonVehicle.model,
        style: data.actualBalloonVehicle.style,
      };

      dispatch(setSelectedBalloonVehicle({ modalId: modalId, selectedBalloonVehicle: data.actualBalloonVehicle }));
      dispatch(getBalloonVehicleEnginesThunk({ params: engineRequestParams, modalId }));
      setIsTermAnimated(true);
    }

    if (isBalloonToggleChanged) {
      data.params = data.params || {};
      data.params.loan_term = data.actualLoanTermValuePerTab.value;
      overriddenValues = { loanTerm: data.actualLoanTermValuePerTab };
      setIsTermAnimated(loanTerm.value !== data.actualLoanTermValuePerTab.value);
    }

    if (isRecalculateRequired) {
      await recalculateLoanData(errorFields, dirtyFields, data);
    } else if (errorFields.length > 0) {
      setIsShowErrorInDependentFields(true);
      const isMileageMissing = errorFields.includes(LOAN_VARIABLES_FIELDS_NAMES.MILEAGE) || Number.isNaN(mileage);

      if (data.isBalloonToggleSwitched && !isMileageMissing) {
        await recalculateLoanData(errorFields, dirtyFields, data, true);
        dispatch(setIsPendingRecalculation({ modalId, isPendingRecalculation: false }));

        return;
      }

      if (
        isSetErrorInCalcFields ||
        isSetErrorInTaxes ||
        isSetErrorInMileage ||
        data.isSpinerAnimated ||
        isAfgTrimChanged
      ) {
        await showErrorInDependentFields(errorFields, data.cursorData, dirtyFields, overriddenValues);
        setIsTaxesAffectingFields(data.isTaxesRecalculateRequired);
      } else if (data.isBalloonRelatedChange) {
        stopRecalculateAnimations();
      }
    }

    dispatch(setIsPendingRecalculation({ modalId, isPendingRecalculation: false }));
  };

  const {
    debounced: debouncedRecalculateLoanVariables,
    cancel: cancelPendingDebouncedRecalculate,
  } = useDebounceCallback(recalculateLoanVariablesCallback, RECALCULATE_DELAY);

  const cancelDebouncedRecalculate = useCallback(() => {
    dispatch(setIsPendingRecalculation({ modalId, isPendingRecalculation: false }));
    debouncedRecalculateLoanVariables({ isRecalculateRequired: false });
  }, [debouncedRecalculateLoanVariables, dispatch, modalId]);

  const isSalesFeesFormChanged = useCallback(() => {
    const prevValues = currentDefaultFormValues.loanVariablesForm.salesFeesForm;
    const currentValues = getValues().loanVariablesForm.salesFeesForm;

    return Object.keys(prevValues).some((field) => prevValues[field] !== currentValues[field]);
  }, [currentDefaultFormValues, getValues]);

  const isMonthlyIncomeFormChanged = useCallback(() => {
    const prevValues = currentDefaultFormValues.loanVariablesForm.monthlyIncomeForm.income;
    const currentValues = getValues().loanVariablesForm.monthlyIncomeForm.income;

    return Object.keys(prevValues).some(
      (field) =>
        prevValues[field] !== currentValues[field] &&
        !(
          (prevValues[field] === 0 || Number.isNaN(prevValues[field])) &&
          (currentValues[field] === 0 || Number.isNaN(currentValues[field]))
        ),
    );
  }, [currentDefaultFormValues, getValues]);

  const isNetTradeInFormChanged = useCallback(
    (exemptFields: string[] = []) => {
      const prevValues = currentDefaultFormValues.loanVariablesForm.netTradeInForm;
      const currentValues = getValues().loanVariablesForm.netTradeInForm;
      const isTradeInValueChanged =
        prevValues.tradeInValue !== currentValues.tradeInValue &&
        !(
          (prevValues.tradeInValue === 0 || Number.isNaN(prevValues.tradeInValue)) &&
          (currentValues.tradeInValue === 0 || Number.isNaN(currentValues.tradeInValue))
        );
      const isAmountOwnedOnTradeChanged =
        prevValues.amountOwnedOnTrade !== currentValues.amountOwnedOnTrade &&
        !(
          (prevValues.amountOwnedOnTrade === 0 || Number.isNaN(prevValues.amountOwnedOnTrade)) &&
          (currentValues.amountOwnedOnTrade === 0 || Number.isNaN(currentValues.amountOwnedOnTrade))
        );

      return isTradeInValueChanged || (isAmountOwnedOnTradeChanged && !exemptFields.includes('amountOwnedOnTrade'));
    },
    [currentDefaultFormValues, getValues],
  );

  const handleChangeCallback = useCallback(
    (event: ChangeEvent<HTMLInputElement> = {} as ChangeEvent<HTMLInputElement>) => {
      const name = event?.target?.name;
      const invokedFromModalClose = !event?.target;
      const formDirtyFields = dirtyFields?.loanVariablesForm || {};

      if (isSalespersonLimitedView && name === LOAN_VARIABLES_FIELDS_NAMES.GUIDEBOOK_VALUE) {
        return;
      }

      const hasExternalFormChanges =
        isSalesFeesFormChanged() || isMonthlyIncomeFormChanged() || isNetTradeInFormChanged();

      // In case if mini-modal (income, fees modals etc.) is closed now, but was not changed - do not recalculate and just exit
      if (invokedFromModalClose && !hasExternalFormChanges && Object.keys(formDirtyFields).length === 0) {
        return;
      }

      const taxesAffectingFields = ['retailPrice', 'addOns', 'vsc', ...(skipRouteOneGap ? [] : ['gap'])].filter(
        (field) => !taxExemptFields?.includes(field),
      );
      const residualValuesAffectingFields = ['engine', 'mileage', 'balloonToggle'];
      const { engine, balloonToggle, mileage, afgTrim } = getValues().loanVariablesForm;

      const isMileageFieldChanged = Object.keys(formDirtyFields).includes('mileage');

      if (isMileageFieldChanged && onOdometerChange) {
        onOdometerChange(mileage);
      }

      const isBalloonToggleChanged = Object.keys(formDirtyFields).includes('balloonToggle');
      const isBalloonToggleCallback = name === LOAN_VARIABLES_FIELDS_NAMES.BALLOON_TOGGLE;
      const isApplyCpoCallback = name === LOAN_VARIABLES_FIELDS_NAMES.APPLY_CPO;
      const isAfgTrimChanged = Object.keys(formDirtyFields).includes('afgTrim');
      const isAddVehicleFormChanged = name === ADD_VEHICLE_FORM_PREFIX;
      const isBalloonRelatedChange = isBalloonToggleChanged || isAfgTrimChanged || isBalloonToggleCallback;

      const isTaxesRecalculateRequired =
        isTaxesAffectingFields ||
        (formDirtyFields &&
          taxesAffectingFields.some((field) => field in formDirtyFields) &&
          !isAddVehicleFormChanged) ||
        isNetTradeInFormChanged(taxExemptFields);

      const isResidualsRequestRequired =
        balloonToggle && formDirtyFields && residualValuesAffectingFields.some((field) => field in formDirtyFields);

      const actualBalloonVehicle = balloonVehicleInfo[afgTrim.value];
      // The term a quote taken at Retail Price 0 fell back to was nobody's choice, so the deal
      // source's term gets first refusal on the first reprice that actually runs -- the backend
      // keeps it only if it is eligible. Answering the missing-information modal is also the deal
      // arriving complete rather than the dealer restructuring it, so the lender keeps the rate it
      // approved. A term the dealer picks themselves settles the question outright (SWA-8055).
      const dealSourceTerm = dealSourceData?.finance_details?.term;

      if (name === LOAN_VARIABLES_FIELDS_NAMES.LOAN_TERM || 'loanTerm' in formDirtyFields) {
        isTermProvisionalRef.current = false;
      }

      const isTermProvisional = isTermProvisionalRef.current;

      const recalculateData = {
        isTaxesRecalculateRequired: isTaxesRecalculateRequired,
        cursorData: { field: event?.target, cursorPosition: event?.target?.selectionStart },
        isResidualsRequestRequired: isResidualsRequestRequired,
        balloonResidualValues: balloonResidualValues,
        actualBalloonVehicle: isAfgTrimChanged ? actualBalloonVehicle : selectedBalloonVehicle,
        residualRequestParams: getResidualRequestParams(
          isAfgTrimChanged ? actualBalloonVehicle : selectedBalloonVehicle,
          mileage,
          engine,
        ),
        isSpinerAnimated: isSpinerAnimated,
        actualLoanTermValuePerTab: balloonToggle ? loanTermPerTab.balloon : loanTermPerTab.retail,
        dealSourceData: dealSourceData,
        isBalloonRelatedChange: isBalloonRelatedChange,
        isBalloonToggleSwitched: isBalloonToggleCallback,
        ...(isTermProvisional && {
          isTermProvisional: true,
          params: {
            ...(dealSourceTerm && { loan_term: dealSourceTerm }),
            ...(isAddVehicleFormChanged && { is_deal_structure_modified: false }),
          },
        }),
      };

      const shouldRecalculateImmediately = isBalloonToggleCallback || isApplyCpoCallback || isAddVehicleFormChanged;
      const recalculateGeneration = ++recalculateGenerationRef.current;

      if (shouldRecalculateImmediately) {
        cancelPendingDebouncedRecalculate();
      }

      const recalculateCallback = shouldRecalculateImmediately
        ? recalculateLoanVariablesCallback
        : debouncedRecalculateLoanVariables;

      return handleSubmit(
        () => {
          if (recalculateGeneration !== recalculateGenerationRef.current) {
            return;
          }

          const forceByMissingOrInvalidInfo =
            availableDealStructureStatus === AVAILABLE_DEAL_STRUCTURE_STATUSES.MISSING_OR_INVALID_INFO &&
            !invokedFromModalClose;

          const isRecalculateRequired =
            Object.keys(formDirtyFields).length > 0 ||
            isBalloonToggleCallback ||
            isApplyCpoCallback ||
            hasExternalFormChanges ||
            forceByMissingOrInvalidInfo ||
            isAddVehicleFormChanged;

          if (recalculateCallback === debouncedRecalculateLoanVariables && isRecalculateRequired) {
            dispatch(setIsPendingRecalculation({ modalId, isPendingRecalculation: true }));
          }

          return recalculateCallback({
            dirtyFields: formDirtyFields,
            isRecalculateRequired: isRecalculateRequired,
            data: recalculateData,
          });
        },

        (errors) => {
          if (recalculateGeneration !== recalculateGenerationRef.current) {
            return;
          }

          const errorFields = Object.values(errors.loanVariablesForm).map((item: any) => item.ref.name);
          const formErrors = [...errorFields];
          const isMileageChanged = Object.keys(formDirtyFields).includes('mileage');

          if (formErrors.includes(LOAN_VARIABLES_FIELDS_NAMES.CONTRACT_DATE)) {
            formErrors.splice(formErrors.indexOf(LOAN_VARIABLES_FIELDS_NAMES.CONTRACT_DATE), 1);
          }

          if (formErrors.includes(LOAN_VARIABLES_FIELDS_NAMES.FIRST_PAYMENT_DATE)) {
            formErrors.splice(formErrors.indexOf(LOAN_VARIABLES_FIELDS_NAMES.FIRST_PAYMENT_DATE), 1);
          }

          if (formErrors.includes(LOAN_VARIABLES_FIELDS_NAMES.LENDER_LIST)) {
            formErrors.splice(formErrors.indexOf(LOAN_VARIABLES_FIELDS_NAMES.LENDER_LIST), 1);
          }

          if (formErrors.includes(LOAN_VARIABLES_FIELDS_NAMES.TAXES) && isTaxesRecalculateRequired) {
            formErrors.splice(formErrors.indexOf(LOAN_VARIABLES_FIELDS_NAMES.TAXES), 1);
          }

          if (
            (isMileageChanged || isBalloonRelatedChange) &&
            formErrors.includes(LOAN_VARIABLES_FIELDS_NAMES.LOAN_TERM)
          ) {
            formErrors.splice(formErrors.indexOf(LOAN_VARIABLES_FIELDS_NAMES.LOAN_TERM), 1);
          }

          if (
            isBalloonRelatedChange &&
            balloonToggle &&
            formErrors.includes(LOAN_VARIABLES_FIELDS_NAMES.ANNUAL_MILEAGE)
          ) {
            formErrors.splice(formErrors.indexOf(LOAN_VARIABLES_FIELDS_NAMES.ANNUAL_MILEAGE), 1);
          }

          const forceByMissingOrInvalidInfo =
            formErrors.length === 0 &&
            availableDealStructureStatus === AVAILABLE_DEAL_STRUCTURE_STATUSES.MISSING_OR_INVALID_INFO &&
            !invokedFromModalClose;

          const isRecalculateRequired =
            formErrors.length === 0 &&
            (Object.keys(formDirtyFields).length > 0 ||
              isBalloonToggleCallback ||
              isApplyCpoCallback ||
              hasExternalFormChanges ||
              forceByMissingOrInvalidInfo);

          if (recalculateCallback === debouncedRecalculateLoanVariables && isRecalculateRequired) {
            dispatch(setIsPendingRecalculation({ modalId, isPendingRecalculation: true }));
          }

          return recalculateCallback({
            dirtyFields: formDirtyFields,
            errorFields: errorFields,
            isRecalculateRequired: isRecalculateRequired,
            data: recalculateData,
          });
        },
      )();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      handleSubmit,
      dirtyFields,
      debouncedRecalculateLoanVariables,
      cancelPendingDebouncedRecalculate,
      availableDealStructureStatus,
      isTaxesAffectingFields,
      taxExemptFields,
      skipRouteOneGap,
      isSalesFeesFormChanged,
      isMonthlyIncomeFormChanged,
      isNetTradeInFormChanged,
      selectedBalloonVehicle,
      loanVariables,
      getValues,
      balloonResidualValues,
      balloonVehicleInfo,
      isSpinerAnimated,
      loanTermPerTab,
      onOdometerChange,
      dealSourceData,
    ],
  );

  const { generateFields, generatedFields } = useGeneratedFields(
    isLender,
    balloonToggle,
    isModalSalesFeesDialogActive,
    isIncomeDialogActive,
    isNetTradeInDialogOpen,
    cancelDebouncedRecalculate,
    isFieldChanged,
    setHistoryCallback,
    getValues,
    balloonVehicleInfo,
    balloonVehicleEngines,
    balloonMaxOdometers,
    balloonResidualValues,
    setValue,
    overrideFormHistoryValues,
    trigger,
    handleChangeCallback,
    clearErrors,
    termsMatchedWithBalloon,
    activeLoanTermsList,
    handleSalesFeesDialogOpen,
    handleIncomeDialogOpen,
    handleNetTradeInDialogOpen,
    handleBackAddsDialogOpen,
    lenderControls,
    maxBackEnd,
    loanVariables,
    errors,
    user,
    manuallyCreated,
    maxPaymentValue,
    payment,
    dealSourceData,
    afgTrim,
    maxMileage,
    regionControls,
    guidebookValue,
    defaultParticipation,
    maxParticipation,
    availableDealStructureStatus,
    isAdjustDealModalAnimated,
    isTaxesAnimated,
    isSpinerAnimated,
    isTermAnimated,
    dealSourceDataFlags,
    isSalespersonLimitedView,
    isShopByCallbackMode,
    contractDate,
    addendumStatus === AddendumStatus.ACTIVE,
    setAddendumStep,
    resetDealSourceDataFlags,
    firstPaymentDaysRange,
    submittedTermMismatch,
  );

  const filterFieldsByRole = useDealStructureFieldsByRole();

  const onSalesFeesCalculatorSubmit = useCallback(
    (data: ISalesFeesForm) => {
      dispatch(setSalesFees(data));
    },
    [dispatch],
  );

  const onNetTradeInCalculatorSubmit = useCallback(
    (data: INetTradeInForm) => {
      dispatch(setNetTradeIn(data));
    },
    [dispatch],
  );

  const validateModalBeforeSubmit = () =>
    isShowLenderList ? validateLenderList(lenderList, setIsValidLenderList) : true;

  const handleSubmitModalClick = () => {
    if (!validateModalBeforeSubmit()) return;

    if (isRenderAddendumFields && createAddendum) {
      handleAddendumFlow(
        lenderState.afg_id!,
        afgDealerId!,
        dealSourceData,
        selectedBalloonVehicle!,
        quoteIdRef,
        loanVariables,
        getValues,
        sendRouteOneData,
        addendumStatus,
        addendumStep,
        setAddendumStatus,
        setAddendumStep,
      );
    } else {
      sendRouteOneData();
    }
  };

  const getChangedFields = (obj1: any, obj2: any): string[] => {
    const changedFields: string[] = [];

    if (!obj1 || !obj2) return changedFields;

    const keysForComparison = new Set([...Object.keys(obj1), ...Object.keys(obj2)]);

    keysForComparison.forEach((key) => {
      if (JSON.stringify(obj1[key]) !== JSON.stringify(obj2[key])) {
        changedFields.push(key);
      }
    });

    return changedFields;
  };

  const handleUndo = useCallback(async () => {
    if (formHistory.length > 1) {
      const currentState = formHistory[formHistory.length - 1] as any;
      const previousState = formHistory[formHistory.length - 2] as any;
      const price = previousState.loanVariablesForm.price;
      const changedFields = getChangedFields(currentState.loanVariablesForm, previousState.loanVariablesForm);
      const calculationChangedFields = changedFields.filter((field) => CALCULATION_FIELD_NAMES.includes(field));
      const isRecalculateRequired = calculationChangedFields.length > 0;

      setToggled(price !== LOAN_VARIABLES_FIELDS_NAMES.RETAIL_PRICE);
      setAddendumStep(0);

      setFormHistory(formHistory.slice(0, formHistory.length - 1));

      if (onOdometerChange) {
        onOdometerChange(previousState.loanVariablesForm.mileage);
      }

      dispatch(
        setLoanTermsList({
          modalId: modalId,
          loanTermsList: previousState.loanVariablesForm.loanTermsList,
        }),
      );
      setIsUndoClicked(isUndoClicked || isRecalculateRequired);

      const { currentPayment, totalAmountFinanced, apr } = getValues().loanVariablesForm;
      const resetData = {
        loanVariablesForm: {
          ...previousState.loanVariablesForm,
          ...(previousState.loanVariablesForm.currentPayment !== EMPTY_STRING && { currentPayment: currentPayment }),
          ...(previousState.loanVariablesForm.totalAmountFinanced !== EMPTY_STRING && {
            totalAmountFinanced: totalAmountFinanced,
          }),
          apr: apr,
        },
      };

      reset(resetData);
      setCurrentDefaultFormValues(resetData);
      await trigger();

      if (isRecalculateRequired) {
        handleSubmit(() =>
          debouncedRecalculateLoanVariables({
            isRecalculateRequired: true,
            data: {
              isResidualsRequestRequired: previousState.loanVariablesForm.balloonToggle,
              residualRequestParams: getResidualRequestParams(
                selectedBalloonVehicle,
                previousState.loanVariablesForm.mileage,
                previousState.loanVariablesForm.engine,
              ),
              dealSourceData: dealSourceData,
            },
          }),
        )();
      }
    }
  }, [
    formHistory,
    reset,
    setFormHistory,
    setLoanTermsList,
    debouncedRecalculateLoanVariables,
    handleSubmit,
    getValues,
    trigger,
    dispatch,
    selectedBalloonVehicle,
    modalId,
    isUndoClicked,
    onOdometerChange,
    dealSourceData,
  ]);

  const getColumns = (): TableColumn[] => {
    if (isLender) {
      let columns = LENDER_LOAN_VARIABLES_COLUMNS as TableColumn[];

      if (!shouldShowCustomerRate)
        columns = columns.filter((column) => LENDER_LOAN_VARIABLES_CUSTOMER_RATE.id !== column.id);

      if (hidesEstimatedPayment)
        columns = columns.filter((column) => LOAN_VARIABLES_MODAL_FIELDS.MONTHLY_PAYMENT !== column.id);

      if (manuallyCreated || user?.withWholesaleInventory) {
        columns = columns.filter((column) => LOAN_VARIABLES_MODAL_FIELDS.PROFIT !== column.id);
      }

      columns = columns.filter((column) =>
        ltvTaxesFeesFilter === 'true'
          ? column.id !== LOAN_VARIABLES_MODAL_FIELDS.LTV_EXC
          : column.id !== LOAN_VARIABLES_MODAL_FIELDS.LTV_INC,
      );

      columns = filterFieldsByRole(columns, user?.role);

      if (columns[1].id === LOAN_VARIABLES_MODAL_FIELDS.EXTRA_CASH_OR_DISCOUNT) {
        columns[1].Header =
          availableDealStructureStatus === AVAILABLE_DEAL_STRUCTURE_STATUSES.OUT_OF_POLICY ? (
            <span style={{ color: COLORS.RED_7 }}>*{t('components.loanTermTable.columns.extraCash')}</span>
          ) : (
            t('components.loanTermTable.columns.extraCash')
          );
      }

      return columns.map((column) => {
        if (column.id !== LOAN_VARIABLES_MODAL_FIELDS.CUSTOMER_RATE) {
          return column;
        }

        const withHeader = {
          ...column,
          Header: loanVariables?.is_estimated_apr
            ? t('components.loanTermTable.columns.calculatedCustomerRate')
            : t('components.loanTermTable.columns.customerRate'),
        };

        if (!showsDecisionRatePrompt) return withHeader;

        // Same scope as the Step 2 resubmit note (SWA-7959): the other statuses need the deal
        // fixed first, so they keep the plain dash every other empty cell shows.
        const isInPolicy = availableDealStructureStatus === AVAILABLE_DEAL_STRUCTURE_STATUSES.IN_POLICY;

        return {
          ...withHeader,
          Cell: (cell: Cell<ILTVValues>) =>
            cell.row.original.customer_rate == null && isInPolicy
              ? t('components.loanVariablesModal.labels.resubmitForPricing')
              : percentFormatter(cell.row.original.customer_rate as number),
        };
      });
    }

    let columns = [
      ...(LOAN_VARIABLES_COLUMNS_MAP.get(price || LOAN_VARIABLES_FIELDS_NAMES.RETAIL_PRICE) as TableColumn[]),
    ];

    if (manuallyCreated || !user?.isWithInventory) {
      columns = columns.filter((column) => LOAN_VARIABLES_MODAL_FIELDS.PROFIT !== column.id);
    }

    if (columns[1].id === LOAN_VARIABLES_MODAL_FIELDS.EXTRA_CASH) {
      columns[1].Header =
        availableDealStructureStatus === AVAILABLE_DEAL_STRUCTURE_STATUSES.OUT_OF_POLICY ? (
          <span style={{ color: COLORS.RED_7 }}>*{t('components.loanTermTable.columns.extraCash')}</span>
        ) : (
          t('components.loanTermTable.columns.extraCash')
        );
    }

    return columns;
  };

  const sendRouteOneData = useCallback(
    async (submitCallbackDelay?: number) => {
      // SbB+/SbC+ lenders have no balloon program on RouteOne, so the deal always goes as retail.
      const isBalloonSubmit = balloonToggle && !isDealerSsoSbbOrSbcFlow;
      const data: ISendRouteOneData = {
        approved_monthly_payment: payment,
        cash_down_payment: cashDown,
        rebates: rebates,
        sales_fees: salesFeesForm.salesDocumentFees,
        add_ons: addOns,
        current_price: retailPrice,
        odometer: mileage,
        guidebook_value: guidebookValue,
        guidebook: loanVariables.guidebook,
        used_guidebook: isLender ? loanVariables.used_guidebook : loanVariables.guidebook,
        loan_term: loanTerm.value,
        vsc: isAllyLender ? 0 : vsc,
        gap: skipRouteOneGap ? 0 : gap,
        taxes: taxes,
        title_reg_license: salesFeesForm.titleRegistrationLicense,
        vin: loanVariables.vin,
        apr: apr,
        year: netTradeInForm.year?.value === OTHER_OPTION.value ? netTradeInForm.otherYear : netTradeInForm.year?.value,
        make: netTradeInForm.make?.value === OTHER_OPTION.value ? netTradeInForm.otherMake : netTradeInForm.make?.value,
        model:
          netTradeInForm.model?.value === OTHER_OPTION.value ? netTradeInForm.otherModel : netTradeInForm.model?.value,
        primary_monthly_income: monthlyIncomeForm.income.primaryMonthlyIncome,
        primary_other_monthly_income: monthlyIncomeForm.income.primaryOtherMonthlyIncome || 0,
        coApplicant_monthly_income: monthlyIncomeForm.income.coApplicantMonthlyIncome || 0,
        coApplicant_other_monthly_income: monthlyIncomeForm.income.coApplicantOtherMonthlyIncome || 0,
        vehicle_id: vehicle_id,
        total_amount_financed: isLender
          ? isAllyLender || isMtbLender
            ? loanVariables.total_financed_exc_back_end ?? loanVariables.total_financed
            : loanVariables.total_financed
          : loanVariables.ltv_categories[0].loan_amount,
        transaction_type: isBalloonSubmit ? DealTransactionType.BALLOON_PAYMENT : DealTransactionType.RETAIL,
      };

      if (netTradeInForm.lienHolder) {
        data.lien_holder = netTradeInForm.lienHolder;
      }

      if (netTradeInForm.tradeInValue) {
        data.value = netTradeInForm.tradeInValue;
      }

      if (netTradeInForm.amountOwnedOnTrade) {
        data.amount_owed = netTradeInForm.amountOwnedOnTrade;
      }

      if (isBalloonSubmit) {
        data.residual_value = balloonPayment;
      }

      if (isShowLenderList) {
        data.lender_list = lenderList;
        data.initiator_lender_id = lenderState?.id;
      }

      const r1LenderId = getSubmitRouteOneLenderId(
        isLender ? lenderState?.route_one_id : undefined,
        dealSourceData.lender,
      );

      data.conversation_id = routeOneAppFormatter(dealSourceData.conversation_id);
      data.dealer_id = dealSourceData.dealer_id;
      data.r1_lender_id = r1LenderId;
      data.r1_user_id = routeOneUserId;

      const response = await dispatch(
        submitDealSourceDataThunk({ dealSource: DealSourceProvider.ROUTE_ONE, body: data }),
      );

      if (response.meta.requestStatus === RequestStatus.FULFILLED) {
        const showSuccess = () => {
          if (gamificationEnabled) {
            dispatch(
              trackActivityThunk({
                activity_type: 'submission',
                metadata: {
                  lender: { type: 'route_one_id', id: r1LenderId },
                  ...(isRac && { is_rac: true }),
                },
              }),
            );
          }

          setShowSuccessSubmit(true);

          setTimeout(() => {
            handleHideWithResetVehicle();

            if (user?.is_sso && !user?.isAlternativeSso) {
              if (!user?.isWithInventory) dispatch(logoutUserThunk());
              window.close();
            }
          }, CLOSE_AFTER_SUCCESS_SUBMIT_DELAY);
        };

        if (submitCallbackDelay) {
          setTimeout(showSuccess, submitCallbackDelay);
        } else {
          showSuccess();
        }
      }

      return response;
    },
    [
      payment,
      cashDown,
      rebates,
      salesFeesForm.salesDocumentFees,
      salesFeesForm.titleRegistrationLicense,
      addOns,
      retailPrice,
      mileage,
      guidebookValue,
      loanVariables,
      loanTerm.value,
      vsc,
      isAllyLender,
      isMtbLender,
      skipRouteOneGap,
      gap,
      taxes,
      apr,
      netTradeInForm.year?.value,
      netTradeInForm.otherYear,
      netTradeInForm.make?.value,
      netTradeInForm.otherMake,
      netTradeInForm.model?.value,
      netTradeInForm.otherModel,
      netTradeInForm.lienHolder,
      netTradeInForm.tradeInValue,
      netTradeInForm.amountOwnedOnTrade,
      monthlyIncomeForm.income.primaryMonthlyIncome,
      monthlyIncomeForm.income.primaryOtherMonthlyIncome,
      monthlyIncomeForm.income.coApplicantMonthlyIncome,
      monthlyIncomeForm.income.coApplicantOtherMonthlyIncome,
      vehicle_id,
      isLender,
      dealSourceData.conversation_id,
      dealSourceData.dealer_id,
      lenderState?.id,
      lenderState?.route_one_id,
      dealSourceData.lender,
      user?.is_sso,
      user?.isAlternativeSso,
      user?.isWithInventory,
      routeOneUserId,
      dispatch,
      handleHideWithResetVehicle,
      balloonToggle,
      balloonPayment,
      isDealerSsoSbbOrSbcFlow,
      isShowLenderList,
      lenderList,
      gamificationEnabled,
      isRac,
    ],
  );

  useEffect(() => {
    const maxValue = maxPaymentValue();

    if (payment > maxValue && isFieldChanged('monthlyIncome') && (!isLender || !isShopByCallbackMode)) {
      setValue(LOAN_VARIABLES_FIELDS_NAMES.PAYMENT, maxValue);
    }
  }, [setValue, isFieldChanged, maxPaymentValue, payment, t, isLender, isShopByCallbackMode]);

  useEffect(() => {
    if (isModalSalesFeesDialogActive || isIncomeDialogActive || isNetTradeInDialogOpen || isBackAddsDialogActive) {
      cancelDebouncedRecalculate();
    }
  }, [
    isModalSalesFeesDialogActive,
    isIncomeDialogActive,
    isNetTradeInDialogOpen,
    isBackAddsDialogActive,
    cancelDebouncedRecalculate,
  ]);

  const handleInitialBalloonValuesApplied = useCallback(() => setAreInitialBalloonValuesApplied(true), []);

  useBalloonVehicleInfoFetchedCallback(
    isLender,
    balloonToggle,
    handleChangeCallback,
    setValue,
    overrideFormHistoryValues,
    handleInitialBalloonValuesApplied,
  );

  useEffect(() => {
    isAwaitingInitialBalloonSetupRef.current = isAwaitingInitialBalloonSetup;
  }, [isAwaitingInitialBalloonSetup]);

  /*
   * When balloon payment is on by default the modal keeps showing its own loader with no fields
   * rendered until the balloon data has been fetched and applied to the calculation, so neither the
   * balloon row nor the deal structures ever flash a loader of their own.
   */
  useEffect(() => {
    if (!isAwaitingInitialBalloonSetup || !areInitialBalloonValuesApplied) return;

    if (isAdjustDealModalAnimated || isFetchingBalloonResidualValues) return;

    onInitialBalloonSetupComplete?.();
  }, [
    isAwaitingInitialBalloonSetup,
    areInitialBalloonValuesApplied,
    isAdjustDealModalAnimated,
    isFetchingBalloonResidualValues,
    onInitialBalloonSetupComplete,
  ]);

  useRequestBalloonFirstPaymentDays(
    balloonToggle,
    afgDealerId,
    lenderState?.afg_id ?? null,
    contractDate,
    setValue,
    overrideFormHistoryValues,
    setFirstPaymentDaysRange,
  );

  useEffect(() => {
    if (isLender) {
      const tierTerms: ILenderTierTerm[] = (commonGetValues().tier as any).tier_terms;

      const overrideParticipationArray = tierTerms
        .filter((term) => {
          const vehicleYear =
            Number(currentVehicleData?.description.split(' ')[0]) || Number(dealSourceData?.sale_vehicle?.year);

          return term.term_in_months === loanTerm.value && term.model_year === vehicleYear;
        })
        .map((term) => term.override_participation);

      const maxValue = overrideParticipationArray.length > 0 ? Math.max(...overrideParticipationArray) : 0;

      if (!maxValue) return;

      setMaxParticipation(maxValue);
    }
  }, [
    commonGetValues,
    currentVehicleData?.description,
    isLender,
    loanTerm.value,
    participation,
    dealSourceData?.sale_vehicle?.year,
    t,
  ]);

  const isShowBackdrop = useCallback(
    () =>
      !isModalPageViewWidth &&
      (isModalSalesFeesDialogActive ||
        isIncomeDialogActive ||
        isNetTradeInDialogOpen ||
        isBackAddsDialogActive ||
        showAddVehicleDialog ||
        isUnlockInventoryDialogActive),
    [
      isModalPageViewWidth,
      isModalSalesFeesDialogActive,
      isIncomeDialogActive,
      isNetTradeInDialogOpen,
      isBackAddsDialogActive,
      showAddVehicleDialog,
      isUnlockInventoryDialogActive,
    ],
  );

  return (
    <FormProvider {...formMethods}>
      <ModalBackdrop showBackdrop={isShowBackdrop()} />
      <LoanVariablesModalHeader
        showSuccessSubmit={showSuccessSubmit}
        uppercaseTitle={uppercaseTitle}
        modalTitle={modalTitle}
        handleUndo={handleUndo}
        formHistory={formHistory}
        handleHideWithResetVehicle={handleHideWithResetVehicle}
        handleViewWorksheet={handleViewWorksheet}
        isAdjustDealModalAnimated={isAdjustDealModalAnimated}
        isPendingRecalculation={isPendingRecalculation}
        isGeneratingWorksheet={isGeneratingWorksheet}
        isLender={isLender}
        availableDealStructureStatus={availableDealStructureStatus}
        isAddendumActive={addendumStatus === AddendumStatus.ACTIVE}
      />
      <FormProvider {...formMethods}>
        <Content style={{ paddingTop: '10px', display: 'flex', flexDirection: 'column' }}>
          <MainContentWrapper>
            <div>
              <LoanVariablesFields
                columns={getColumns()}
                loanVariables={loanVariables}
                showSuccessSubmit={showSuccessSubmit}
                generateFields={generateFields}
                generatedFields={generatedFields}
                tier={tierName}
                manuallyCreated={manuallyCreated}
                isLender={isLender}
                isAdjustDealModalAnimated={isAdjustDealModalAnimated}
                dealSourceData={dealSourceData}
                dealSourceDataFlags={dealSourceDataFlags}
                availableDealStructureStatus={availableDealStructureStatus}
                isMakeItWork={isMakeItWork}
                isRenderRouteOneUserId={shouldRenderRouteOneUserId && !isDownloadAddendumStep}
                isSubmitButtonDisabled={
                  (isUndoClicked ? true : necessaryDirtyFields.length > 0) ||
                  isAdjustDealModalAnimated ||
                  addendumStatus === AddendumStatus.ACTIVE ||
                  (shouldRenderRouteOneUserId && !isDownloadAddendumStep && !routeOneUserId) ||
                  !canSubmitDealStructure ||
                  (canSubmitDeal && !dealSourceData?.conversation_id) ||
                  (isRenderAddendumFields && createAddendum && (!contractDate || !firstPaymentDate))
                }
                isShowExtraCashWarn={!isExtraCashAvailable && !isShowErrorInDependentFields}
                maxAmountFinancedWarn={maxAmountFinancedWarn}
                handleSubmitModalClick={handleSubmitModalClick}
                resetLoanVariablesFormValues={resetLoanVariablesFormValues}
                isSubmitR1DealBlockedByControls={isSubmitR1DealBlockedByControls}
                isBalloonPaymentAvailable={isBalloonPaymentAvailable}
                isEligibleForAfg={isEligibleForAfg}
                balloonToggle={balloonToggle}
                balloonToggleCallback={(event: ChangeEvent<HTMLInputElement>) =>
                  balloonToggleCallback(
                    event,
                    formMethods,
                    setHistoryCallback,
                    startRecalculateAnimations,
                    balloonVehicleInfoFlags,
                    cancelDebouncedRecalculate,
                    handleChangeCallback,
                    loanTermPerTab,
                    setLoanTermPerTab,
                    loanTerm,
                  )
                }
                isBalloonRowAnimated={isBalloonRowAnimated}
                isFetchingBalloonResidualValues={isFetchingBalloonResidualValues}
                residualFetchFailed={residualFetchFailed}
                isSalespersonLimitedView={isSalespersonLimitedView}
                getActualPaymentInfoMessage={getActualPaymentInfoMessage}
                getLtvCategories={getLtvCategories}
                lenderListOptions={lenderListOptions}
                isShowLenderList={isShowLenderList}
                isValidLenderList={isValidLenderList}
                isClearLenderListButtonDisabled={(lenderList?.length ?? 0) === 0}
                clearLenderList={() => clearLenderList(setValue, isFieldChanged, setHistoryCallback)}
                lenderListChangeCallback={() =>
                  lenderListChangeCallback(setIsValidLenderList, isFieldChanged, setHistoryCallback)
                }
                validateModalBeforeSubmit={validateModalBeforeSubmit}
                isDisplayPolicyStatusMessage={isDisplayPolicyStatusMessage}
                createAddendum={createAddendum}
                afgSubmitDeal={afgSubmitDeal}
                isRenderAddendumFields={isRenderAddendumFields}
                addendumStep={addendumStep}
                addendumStatus={addendumStatus}
                shouldPromptResubmit={shouldPromptResubmit}
                isAllyLender={isAllyLender}
                isResubmitForPricing={isResubmitForPricing}
                canSubmitDealStructure={canSubmitDealStructure}
              />
              {isIncomeDialogActive && (
                <LendersMonthlyIncomeModal
                  show={isIncomeDialogActive}
                  onHide={() => {
                    handleIncomeDialogClose()();
                    handleChangeCallback();
                  }}
                  setFormHistory={setFormHistory}
                  initialValues={monthlyIncomeForm}
                  modalName={LOAN_VARIABLES_FIELDS_NAMES.MONTHLY_INCOME_FORM}
                  totalName={LOAN_VARIABLES_FIELDS_NAMES.MONTHLY_INCOME}
                  isLender={isLender}
                  verticallyCentered
                  setValueDelegate={commonSetValue}
                  isDealSourceData={isMakeItWork && Object.keys(dealSourceData || {}).length > 0}
                  dealSourceData={dealSourceData}
                />
              )}
              {isModalSalesFeesDialogActive && (
                <SaleFeesCalculator
                  title={t('components.loanVariablesModal.fields.salesFees')}
                  onHide={() => {
                    handleSalesFeesDialogClose()();
                    handleChangeCallback();
                  }}
                  show={isModalSalesFeesDialogActive}
                  modalSize={MODAL_DIALOG_WIDTH.CUSTOM.PX_500}
                  totalFieldLabel={t('components.loanVariablesModal.modals.salesFees.fields.totalFees')}
                  totalWidthField={ColumnWidth.S}
                  calculationFields={CALCULATION_FIELDS}
                  onSalesFeesCalculatorSubmit={onSalesFeesCalculatorSubmit}
                  totalName={LOAN_VARIABLES_FIELDS_NAMES.TOTAL_SALES_FEES}
                  formValuesName={LOAN_VARIABLES_FIELDS_NAMES.SALES_FEES_FORM}
                  setFormHistory={setFormHistory}
                />
              )}
              {isNetTradeInDialogOpen && (
                <NetTradeInCalculator
                  title={t('components.loanVariablesModal.fields.netTradeIn')}
                  onHide={() => {
                    handleCloseNetTradeInDialog()();
                    handleChangeCallback();
                  }}
                  show={isNetTradeInDialogOpen}
                  modalSize={MODAL_DIALOG_WIDTH.CUSTOM.PX_425}
                  totalFieldLabel={t('components.loanVariablesModal.modals.netTradeIn.fields.totalNetTradeIn')}
                  fieldWidth={ColumnWidth.M}
                  calculationFields={NET_TRADE_IN_CALCULATION_FIELDS}
                  onNetTradeInCalculatorSubmit={onNetTradeInCalculatorSubmit}
                  totalName={LOAN_VARIABLES_FIELDS_NAMES.NET_TRADE_IN}
                  formValuesName={LOAN_VARIABLES_FIELDS_NAMES.NET_TRADE_IN_FORM}
                  setFormHistory={setFormHistory}
                  isRouteOneFlow={isRouteOneFlow}
                />
              )}
              {isBackAddsDialogActive && (
                <BackAddsCalculator
                  onHide={() => {
                    handleBackAddsDialogClose()();
                    handleChangeCallback();
                  }}
                  show={isBackAddsDialogActive}
                  linePolicy={loanVariables?.line_3_policy}
                  line3={loanVariables?.total_financed_exc_back_end}
                  edc={loanVariables?.edc}
                  maxBackEnd={maxBackEnd}
                  setFormHistory={setFormHistory}
                />
              )}
              {isUnlockInventoryDialogActive && (
                <UnlockInventoryDialog
                  show={isUnlockInventoryDialogActive}
                  onHide={handleUnlockInventoryDialogClose()}
                />
              )}
              {(user?.is_sso || !user?.is_sso) && (
                <AddVehicleDialog
                  tier={tierName}
                  show={showAddVehicleDialog}
                  onHide={() => {
                    setShowAddVehicleDialog(false);
                  }}
                  onHideAnotherModal={handleHide}
                  fromModal={true}
                  lenderName={lenderState?.name}
                  currentLenderId={currentLenderId}
                  tableName={isLender ? TableName.LENDER : TableName.RAC}
                  isDealSourceVehicle={isDealSourceVehicle}
                  initialValues={addVehicleInitValues}
                  dealSourceData={dealSourceData}
                  isMakeItWork={isMakeItWork}
                  setDealSourceData={setDealSourceData}
                  setExternalValue={setValue}
                  dealSourceDataFlags={dealSourceDataFlags}
                  updateCallback={handleChangeCallback}
                  overrideFormHistoryValues={overrideFormHistoryValues}
                />
              )}
            </div>
            {!showSuccessSubmit && (
              <RightSideColumn>
                {(hasGamificationContent || adjustDealLogo) && (
                  <AdSpaceContainer>
                    <AchievementProgressCard
                      noBorder={true}
                      variant="modal"
                      fallbackContent={
                        adjustDealLogo && (
                          <div
                            onClick={handleAdjustDealLogoClick}
                            style={{
                              cursor: adjustDealLogoShouldDisplayUpgrade || adjustDealLogoLink ? 'pointer' : 'default',
                              width: '100%',
                              height: '100%',
                              position: 'relative',
                            }}
                          >
                            <AdSpaceLogo src={adjustDealLogo} alt="Ad Space" />
                            {(adjustDealLogoShouldDisplayUpgrade || adjustDealLogoLink) && (
                              <AdSpaceLinkOutIconWrapper>
                                <AdSpaceLinkOutIcon src={STATIC_IMAGE_URLS.icons.externalLink} alt="" />
                              </AdSpaceLinkOutIconWrapper>
                            )}
                          </div>
                        )
                      }
                    />
                  </AdSpaceContainer>
                )}
                <SuggestedVehiclesBlock
                  viewOtherVehiclesClick={handleHideWithResetVehicle}
                  sortedVehiclesSelector={sortedVehiclesSelector}
                  vehiclesFetchingFlagsSelector={vehiclesFetchingFlagsSelector}
                  startRecalculateAnimations={startRecalculateAnimations}
                  stopRecalculateAnimations={stopRecalculateAnimations}
                  isPendingRecalculation={isPendingRecalculation}
                />
              </RightSideColumn>
            )}
          </MainContentWrapper>
        </Content>
      </FormProvider>
    </FormProvider>
  );
};
